import React, { useState, useEffect } from "react";
import {
  FiUpload,
  FiDownload,
  FiTrash2,
  FiEye,
  FiFile,
  FiCheck,
  FiClock,
  FiFileText,
  FiLink,
  FiAlertTriangle,
  FiRefreshCw,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import { useAuth } from "../hooks/useAuth";
import { useDocuments } from "../hooks/useDocuments";
import { apiClient } from "../services/api";
import { supabase } from "../supabaseClient"; // Added for Supabase fallback
import DuplicateDetectionModal from "./DuplicateDetectionModal";
import DeletionProgressModal from "./DeletionProgressModal";

interface DocumentRequirement {
  id: string;
  name: string;
  description: string;
  status: "pending" | "uploaded";
  isRequired: boolean;
  category: string;
}

interface UploadedDocument {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedAt: Date;
  documentType: string;
  fileUrl: string;
}

interface MergedDocument {
  id: string;
  categoryId: string;
  totalPages: number;
  fileSize: number;
  createdAt: string;
  fileName: string;
}

interface SuccessModalProps {
  isOpen: boolean;
  message: string;
  documentCount: number;
}

interface QualityErrorModalProps {
  isOpen: boolean;
  fileName: string;
  qualityScore: number;
  failureReason: string;
  recommendation: string;
  onClose: () => void;
  requiresConfirmation?: boolean;
  onProceed?: () => void;
}

// Quality Error Modal Component
const QualityErrorModal = ({
  isOpen,
  fileName,
  qualityScore,
  failureReason,
  recommendation,
  onClose,
  requiresConfirmation,
  onProceed,
}: QualityErrorModalProps) => {
  if (!isOpen) return null;

  // DEBUG: Log what props the modal is receiving
  console.log("🔍 MODAL PROPS DEBUG:", {
    isOpen,
    fileName,
    qualityScore,
    requiresConfirmation,
    hasOnProceed: !!onProceed,
  });

  const isLowQuality = requiresConfirmation;

  return (
    <div className="modal-overlay">
      <div className="simple-success-modal">
        <div className="simple-success-content">
          <div className="simple-success-icon error">
            {React.createElement(FiTrash2 as any, {
              style: { color: "#dc2626", fontSize: "24px" },
            })}
          </div>
          <div className="simple-success-message">
            <h4>
              {isLowQuality
                ? "Document Quality Warning"
                : "Document Quality Issue"}
            </h4>
            <div className="quality-error-details">
              <p>
                <strong>Document:</strong> {fileName}
              </p>
              <p>
                <strong>Quality Score:</strong> {Math.round(qualityScore)}/100
              </p>
              <p>
                <strong>Why it failed:</strong> {failureReason}
              </p>
              <p>
                <strong>How to fix:</strong> {recommendation}
              </p>
              {isLowQuality && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "6px",
                    border: "1px solid #f59e0b",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "14px", color: "#92400e" }}>
                    💡 <strong>You can still upload this document</strong> if
                    you believe it's acceptable for your needs. The system
                    detected quality issues, but you have the final say.
                  </p>
            </div>
              )}
          </div>
          </div>
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              gap: "8px",
              justifyContent: "center",
            }}
          >
          <button 
            onClick={onClose}
            className="close-error-btn"
            style={{ 
                padding: "8px 16px",
                backgroundColor: "#6b7280",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              {isLowQuality ? "Cancel" : "Got it"}
          </button>
            {isLowQuality && onProceed && (
              <button
                onClick={onProceed}
                className="proceed-upload-btn"
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#059669",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Upload Anyway
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Documents() {
  const { user } = useAuth();
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    documentId: string | null;
    fileName: string | null;
  }>({
    isOpen: false,
    documentId: null,
    fileName: null,
  });

  // File type modal state
  const [fileTypeModal, setFileTypeModal] = useState<{
    isOpen: boolean;
    documentId: string | null;
    fileName: string | null;
    blob: Blob | null;
  }>({
    isOpen: false,
    documentId: null,
    fileName: null,
    blob: null,
  });

  // File size error modal state
  const [fileSizeErrorModal, setFileSizeErrorModal] = useState<{
    isOpen: boolean;
    fileName: string;
    fileSize: number;
    maxSize: number;
  }>({
    isOpen: false,
    fileName: "",
    fileSize: 0,
    maxSize: 10 * 1024 * 1024,
  });

  // Categorization review modal state
  const [categorizationModal, setCategorizationModal] = useState<{
    isOpen: boolean;
    documents: Array<{
      file: File;
      suggestedCategory: string;
      confidence: number;
      finalCategory: string;
    }>;
  }>({
    isOpen: false,
    documents: [],
  });

  // Processing status state
  const [processingStatus, setProcessingStatus] = useState<{
    isProcessing: boolean;
    progress: number;
    processedCount: number;
    totalCount: number;
    message: string;
  }>({
    isProcessing: false,
    progress: 0,
    processedCount: 0,
    totalCount: 0,
    message: "",
  });

  // Success modal state
  const [successModal, setSuccessModal] = useState<SuccessModalProps>({
    isOpen: false,
    message: "",
    documentCount: 0,
  });

  const [qualityErrorModal, setQualityErrorModal] =
    useState<QualityErrorModalProps>({
    isOpen: false,
      fileName: "",
    qualityScore: 0,
      failureReason: "",
      recommendation: "",
      requiresConfirmation: false,
      onClose: () =>
        setQualityErrorModal((prev) => ({ ...prev, isOpen: false })),
      onProceed: undefined,
    });

  // Track pending uploads for force retry
  const [pendingUpload, setPendingUpload] = useState<{
    file: File;
    documentTypeId: string;
  } | null>(null);

  // PDF Merging States
  const [mergedDocuments, setMergedDocuments] = useState<MergedDocument[]>([]);
  const [selectedDocumentsForMerge, setSelectedDocumentsForMerge] = useState<
    string[]
  >([]);
  const [mergeOrder, setMergeOrder] = useState<"date" | "filename" | "custom">(
    "date"
  );
  const [compressionSize, setCompressionSize] = useState<number>(2); // MB
  const [customFileName, setCustomFileName] = useState<string>();
  const [merging, setMerging] = useState(false);
  const [mergeModal, setMergeModal] = useState({
    isOpen: false,
    categoryId: "",
  });
  
  // Bulk Delete States
  const [selectedDocumentsForDelete, setSelectedDocumentsForDelete] = useState<
    string[]
  >([]);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState<{
    isOpen: boolean;
    documents: Array<{ id: string; fileName: string; documentType: string }>;
  }>({
    isOpen: false,
    documents: [],
  });

  // Bulk Download States
  const [selectedDocumentsForDownload, setSelectedDocumentsForDownload] =
    useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);

  // Deletion Progress Modal State
  const [deletionProgress, setDeletionProgress] = useState<{
    isOpen: boolean;
    isDeleting: boolean;
    totalDocuments: number;
    deletedCount: number;
    currentFileName?: string;
  }>({
    isOpen: false,
    isDeleting: false,
    totalDocuments: 0,
    deletedCount: 0,
    currentFileName: undefined,
  });
  
  // Drag and drop states for custom order
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  // Available IRCC document categories
  const availableCategories = [
    {
      id: "passport",
      name: "Passport",
      description: "Valid passport with at least 6 months validity",
      icon: "📄",
      category: "identity",
    },
    {
      id: "birth_certificate",
      name: "Birth Certificate",
      description: "Official birth certificate with apostille if required",
      icon: "📄",
      category: "identity",
    },
    {
      id: "marriage_certificate",
      name: "Marriage Certificate",
      description: "Marriage certificate if applicable",
      icon: "📄",
      category: "identity",
    },
    {
      id: "employment_letter",
      name: "Employment Letter",
      description: "Current employment verification letter",
      icon: "💼",
      category: "employment",
    },
    {
      id: "bank_statements",
      name: "Bank Statements",
      description: "Last 6 months of bank statements",
      icon: "💰",
      category: "financial",
    },
    {
      id: "tax_returns",
      name: "Tax Returns",
      description: "Last 3 years of tax returns",
      icon: "📊",
      category: "financial",
    },
    {
      id: "medical_examination",
      name: "Medical Examination",
      description: "Medical examination results from panel physician",
      icon: "🏥",
      category: "medical",
    },
    {
      id: "police_clearance",
      name: "Police Clearance",
      description: "Police clearance from all countries lived in",
      icon: "⚖️",
      category: "legal",
    },
    {
      id: "educational_credentials",
      name: "Educational Credentials",
      description: "Educational certificates and transcripts",
      icon: "🎓",
      category: "education",
    },
  ];

  // Selected document categories (dynamic)
  const [selectedCategories, setSelectedCategories] = useState<
    DocumentRequirement[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [customCategoryModal, setCustomCategoryModal] = useState<{
    isOpen: boolean;
    name: string;
    description: string;
  }>({
    isOpen: false,
    name: "",
    description: "",
  });

  // Duplicate detection modal
  const [duplicateModal, setDuplicateModal] = useState<{
    isOpen: boolean;
    duplicateFiles: string[];
  }>({
    isOpen: false,
    duplicateFiles: [],
  });

  // Uploaded documents (bottom panel)
  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocument[]
  >([]);

  // Fetch cases on component mount
  useEffect(() => {
    fetchCases();
  }, []);

  // Fetch documents and selected categories when case is selected
  useEffect(() => {
    if (selectedCase) {
      // Add a small delay to prevent rapid successive calls
      const timeoutId = setTimeout(() => {
        fetchDocuments();
        fetchSelectedCategories();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedCase]);

  // Listen for document refresh events
  useEffect(() => {
    const handleDocumentRefresh = () => {
      if (selectedCase) {
        console.log("Document refresh event received, refreshing documents...");
        // Force clear any cached data and refresh
        setUploadedDocuments([]);
        setMergedDocuments([]);
        fetchDocuments();
        fetchSelectedCategories();
      }
    };

    const handleForceRefresh = () => {
      if (selectedCase) {
        console.log("Force refresh event received, clearing all caches...");
        // Clear all state and force fresh fetch
        setUploadedDocuments([]);
        setMergedDocuments([]);
        setSelectedCategories([]);
        // Add a small delay to ensure state is cleared
        setTimeout(() => {
          fetchDocuments(true); // Force refresh with direct database query
          fetchSelectedCategories();
        }, 100);
      }
    };

    // Add periodic refresh check for deleted documents
    const handlePeriodicRefresh = () => {
      // Only refresh if we're not in the middle of operations
      if (
        selectedCase &&
          uploadedDocuments.length > 0 && 
          !deleteModal.isOpen && 
          !bulkDeleteModal.isOpen && 
          !deleting &&
          !uploading &&
          !merging &&
        !bulkDownloading
      ) {
        console.log("Periodic refresh check for deleted documents...");
        fetchDocuments();
      } else {
        console.log("Skipping periodic refresh - operation in progress");
      }
    };

    window.addEventListener("documents:refresh", handleDocumentRefresh);
    window.addEventListener("documents:force-refresh", handleForceRefresh);
    
    // Set up periodic refresh every 5 seconds when documents are present
    const intervalId = setInterval(handlePeriodicRefresh, 5000);
    
    return () => {
      window.removeEventListener("documents:refresh", handleDocumentRefresh);
      window.removeEventListener("documents:force-refresh", handleForceRefresh);
      clearInterval(intervalId);
    };
  }, [
    selectedCase,
    uploadedDocuments.length,
    deleteModal.isOpen,
    bulkDeleteModal.isOpen,
    deleting,
    uploading,
    merging,
    bulkDownloading,
  ]);

  // Update document requirements whenever uploaded documents change
  useEffect(() => {
    if (selectedCase && uploadedDocuments.length >= 0) {
      // Only update requirements when we actually have documents
      // This prevents unnecessary API calls when the component first loads
      updateDocumentRequirements();
    }
  }, [uploadedDocuments, selectedCase]);

  // Fetch selected categories from database
  const fetchSelectedCategories = async () => {
    if (!selectedCase) return;
    
    try {
      console.log("Fetching selected categories for case:", selectedCase);
      
      // Try API first
      let categories;
      try {
        categories = await apiClient.getSelectedCategories(selectedCase);
        console.log("Categories fetched via API");
      } catch (apiError) {
        console.warn(
          "⚠️ API failed, falling back to direct Supabase call:",
          apiError
        );
        
        // Fallback to direct Supabase call
        const { data, error } = await supabase
          .from("selected_document_categories")
          .select("*")
          .eq("case_id", selectedCase)
          .order("created_at", { ascending: true });
        
        if (error) {
          console.error("❌ Supabase fallback error:", error);
          throw new Error(`Database error: ${error.message}`);
        }
        
        categories = data;
        console.log("Categories fetched via Supabase fallback");
      }
      
      // Transform database format to UI format
      const transformedCategories: DocumentRequirement[] = (categories || [])
        .filter(
          (cat: any, index: number, arr: any[]) =>
            arr.findIndex((c) => c.category_id === cat.category_id) === index
        )
        .map((cat: any) => ({
          id: cat.category_id,
          name: cat.category_name,
          description: cat.category_description || "",
          status: cat.status,
          isRequired: cat.is_required,
          category: cat.category_type,
        }));
      
      setSelectedCategories(transformedCategories);
      console.log("Loaded selected categories:", transformedCategories.length);
    } catch (error) {
      console.error("Error fetching selected categories:", error);
      // Don't show error modal here as it might be a new case with no categories
    }
  };

  const fetchCases = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getCases();
      setCases(data);
      if (data.length > 0 && !selectedCase) {
        setSelectedCase(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (forceRefresh = false) => {
    if (!selectedCase) return;
    
    // Prevent multiple simultaneous requests (unless force refresh)
    if (loading && !forceRefresh) {
      console.log("Skipping fetchDocuments - already loading");
      return;
    }
    
    try {
      // Fetch regular documents (with force refresh bypass)
      let documents;
      if (forceRefresh) {
        console.log(
          "Force refresh: Using direct Supabase query to bypass API cache"
        );
        // Force refresh: Use direct Supabase query to bypass any API caching
        const { data, error } = await supabase
          .from("documents")
          .select("*")
          .eq("case_id", selectedCase)
          .order("uploaded_at", { ascending: false });
        
        if (error) {
          console.error("Direct Supabase query error:", error);
          throw new Error(`Database error: ${error.message}`);
        }
        
        documents = data;
        console.log(
          "Fetched documents via direct Supabase query (force refresh):",
          documents
        );
      } else {
        documents = await apiClient.getDocumentsForCase(selectedCase);
        console.log("Fetched regular documents from API:", documents);
      }
      
      // Fetch merged documents - only fetch cross-category merges by default
      // Individual category merges will be fetched on-demand when needed
      let allMergedDocuments: any[] = [];
      try {
        // Get merged documents for cross-category merges only
        const crossCategoryMerged = await apiClient.getMergedDocuments(
          selectedCase,
          "cross_category_merge"
        );
        if (crossCategoryMerged.data) {
          allMergedDocuments = [
            ...allMergedDocuments,
            ...crossCategoryMerged.data,
          ];
        }
        
        // Only fetch individual category merges if we have regular documents AND we're not in initial load
        // This prevents unnecessary API calls when the page first loads
        if (documents.length > 0 && uploadedDocuments.length > 0) {
          console.log("Fetching individual category merges (have documents)");
          const categories = [
            "bank_statements",
            "tax_returns",
            "educational_credentials",
            "employment_letter",
            "passport",
            "birth_certificate",
            "marriage_certificate",
            "medical_records",
            "police_clearance",
          ];
          
          for (const category of categories) {
            try {
              const mergedDocs = await apiClient.getMergedDocuments(
                selectedCase,
                category
              );
              if (mergedDocs.data && mergedDocs.data.length > 0) {
                allMergedDocuments = [
                  ...allMergedDocuments,
                  ...mergedDocs.data,
                ];
              }
            } catch (error) {
              // Ignore errors for categories that don't have merged documents
              console.log(`No merged documents for category: ${category}`);
            }
          }
        } else {
          console.log(
            "⏭️ Skipping individual category merges (no documents or initial load)"
          );
        }
      } catch (error) {
        console.warn("⚠️ Error fetching merged documents:", error);
      }
      
      // Convert merged documents to the same format as regular documents
      const mergedDocumentsFormatted = allMergedDocuments.map((mergedDoc) => {
        // Always use the actual filename from database
        const fileName =
          mergedDoc.file_name || mergedDoc.fileName || "merged_document.pdf";
        
        console.log("Merged document mapping:", {
          id: mergedDoc.id,
          originalFileName: mergedDoc.file_name,
          displayFileName: fileName,
          categoryId: mergedDoc.categoryId,
        });
        
        return {
          id: mergedDoc.id,
          fileName: fileName,
          fileSize: mergedDoc.fileSize || 0,
          uploadedAt: new Date(mergedDoc.createdAt),
          documentType:
            mergedDoc.categoryId === "cross_category_merge"
              ? "merged_document"
              : mergedDoc.categoryId,
          fileUrl: mergedDoc.file_url || "",
          originalFileName: mergedDoc.file_name, // Keep original for deletion matching
        };
      });
      
      // Combine regular and merged documents
      const allDocuments = [...documents, ...mergedDocumentsFormatted];
      console.log("Combined documents (regular + merged):", allDocuments);
      console.log("Document breakdown:", {
        regularDocuments: documents.length,
        mergedDocuments: mergedDocumentsFormatted.length,
        totalDocuments: allDocuments.length,
        selectedCase,
      });

      // Update state with fresh data from database
      setUploadedDocuments((prev) => {
        // Clean up any stale documents that no longer exist in database
        const currentDocIds = new Set(allDocuments.map((doc) => doc.id));
        const cleanedDocs = prev.filter((doc) => currentDocIds.has(doc.id));

        console.log("FetchDocuments - UI State Update:", {
          previousCount: prev.length,
          newCount: allDocuments.length,
          cleanedCount: prev.length - cleanedDocs.length,
          forceRefresh,
        });
        
        if (cleanedDocs.length !== prev.length) {
          console.log("Cleaned up stale documents from UI state");
          console.log(
            "Previous count:",
            prev.length,
            "New count:",
            allDocuments.length
          );
        }
        
        return allDocuments; // Use the fresh data from database
      });
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const updateDocumentRequirements = async () => {
    if (!selectedCase) return;

    console.log("Updating document requirements...");
    
    // Use the current uploadedDocuments state instead of fetching again
    // This prevents the circular dependency that was causing infinite loops
    console.log("Uploaded documents:", uploadedDocuments);
    console.log("Current requirements:", selectedCategories);

    setSelectedCategories((prev) =>
      prev.map((req) => {
      // Check for exact match first
        let hasUploadedDoc = uploadedDocuments.some(
          (doc) => doc.documentType === req.id
      );

      // If no exact match, check for partial matches
      if (!hasUploadedDoc) {
          hasUploadedDoc = uploadedDocuments.some((doc) => {
          const docType = doc.documentType.toLowerCase();
          const reqId = req.id.toLowerCase();
          
          // Check if document type contains requirement ID or vice versa
          return docType.includes(reqId) || reqId.includes(docType);
        });
      }

      // Additional mapping for common variations (fallback)
      if (!hasUploadedDoc) {
        const mapping: { [key: string]: string[] } = {
            bank_statements: ["bank_statement", "bank_statements"],
            tax_returns: ["tax_return", "tax_returns"],
            employment_letter: ["employment_letter", "employment"],
            marriage_certificate: ["marriage_certificate", "marriage"],
            birth_certificate: ["birth_certificate", "birth"],
            passport: ["passport"],
            educational_credentials: [
              "educational_credentials",
              "education",
              "educational",
              "other",
            ],
            medical_records: ["medical_record", "medical_records"],
            police_clearance: ["police_clearance", "police"],
        };

        const possibleMatches = mapping[req.id] || [];
          hasUploadedDoc = uploadedDocuments.some((doc) =>
          possibleMatches.includes(doc.documentType)
        );
      }

      // Special handling for educational credentials - check for educational keywords in filename
        if (!hasUploadedDoc && req.id === "educational_credentials") {
          hasUploadedDoc = uploadedDocuments.some(
            (doc) =>
              doc.fileName.toLowerCase().includes("education") ||
              doc.fileName.toLowerCase().includes("credential") ||
              doc.fileName.toLowerCase().includes("diploma") ||
              doc.fileName.toLowerCase().includes("degree") ||
              doc.fileName.toLowerCase().includes("transcript")
          );
        }

        const newStatus = hasUploadedDoc ? "uploaded" : "pending";
        console.log(`${req.name}: ${newStatus} (${req.id})`);
      
      // Debug: Show which documents matched
      if (hasUploadedDoc) {
          const matchingDocs = uploadedDocuments.filter((doc) => {
          const docType = doc.documentType.toLowerCase();
          const reqId = req.id.toLowerCase();
            return (
              docType === reqId ||
              docType.includes(reqId) ||
              reqId.includes(docType)
            );
          });
          console.log(
            `   Matching documents:`,
            matchingDocs.map((d) => `${d.fileName} (${d.documentType})`)
          );
      }
      
      return {
        ...req,
          status: newStatus,
      };
      })
    );
  };

  const handleFileUpload = async (
    documentTypeId: string,
    files: FileList | File
  ) => {
    if (!selectedCase) return;
    
    // Convert single file to array for consistent handling
    const fileArray = files instanceof FileList ? Array.from(files) : [files];
    
    // Check file sizes (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const oversizedFiles = fileArray.filter((file) => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      const firstOversizedFile = oversizedFiles[0];
      setFileSizeErrorModal({
        isOpen: true,
        fileName:
          oversizedFiles.length === 1
            ? firstOversizedFile.name
            : `${oversizedFiles.length} files`,
        fileSize: firstOversizedFile.size,
        maxSize,
      });
      return;
    }

    // Check for duplicate filenames before uploading
    const duplicateFiles = fileArray.filter((file) =>
      uploadedDocuments.some((doc) => doc.fileName === file.name)
    );

    if (duplicateFiles.length > 0) {
      setDuplicateModal({
        isOpen: true,
        duplicateFiles: duplicateFiles.map((f) => f.name),
      });
      return;
    }
    
    setUploading(documentTypeId);
    
    try {
      console.log(
        "Starting upload for:",
        documentTypeId,
        "Files:",
        fileArray.map((f) => f.name)
      );
      
      // Upload all files
      const uploadPromises = fileArray.map((file) =>
        apiClient.uploadDocument(selectedCase, documentTypeId, file)
      );
      
      const newDocuments = await Promise.all(uploadPromises);
      
      console.log("Upload successful:", newDocuments);
      
      // Add to uploaded documents with correct category mapping (prevent duplicates)
      const documentsWithCorrectCategory = newDocuments.map((doc) => ({
        ...doc,
        documentType: documentTypeId, // Ensure the document type matches the category it was uploaded under
      }));

      setUploadedDocuments((prev) => {
        // Filter out any existing documents with the same ID to prevent duplicates
        const filteredPrev = prev.filter(
          (existingDoc) =>
            !documentsWithCorrectCategory.some(
              (newDoc) => newDoc.id === existingDoc.id
            )
        );
        return [...documentsWithCorrectCategory, ...filteredPrev];
      });
      
      // Update document requirements
      setSelectedCategories((prev) =>
        prev.map((req) =>
          req.id === documentTypeId ? { ...req, status: "uploaded" } : req
        )
      );

      // Refresh document requirements from backend to ensure status is accurate
      await updateDocumentRequirements();

      // Show success message
      setSuccessModal({
        isOpen: true,
        message: `Successfully uploaded ${newDocuments.length} document${
          newDocuments.length > 1 ? "s" : ""
        }`,
        documentCount: newDocuments.length,
      });
    } catch (error) {
      console.error("Upload error:", error);
      
      // Handle quality check errors
      if (error instanceof Error && (error as any).qualityReport) {
        const qualityReport = (error as any).qualityReport;
        const fileName = fileArray[0]?.name || "Unknown file";
        const requiresConfirmation =
          (error as any).requiresConfirmation || false;

        // DEBUG: Log the error details to see what we're receiving
        console.log("🔍 QUALITY ERROR DEBUG:", {
          errorMessage: error.message,
          qualityScore: qualityReport.score,
          requiresConfirmation: requiresConfirmation,
          hasQualityReport: !!qualityReport,
          errorKeys: Object.keys(error as any),
        });
        
        // Extract the main issue and recommendation
        const mainIssue =
          qualityReport.issues[0] || "Document quality is too low";
        const mainRecommendation =
          qualityReport.recommendations[0] ||
          "Please improve document quality and try again";

        // Store file data for retry (avoid race condition by capturing values directly)
        const retryFile = fileArray[0];
        const retryDocumentTypeId = documentTypeId;

        // Store pending upload for potential retry
        if (requiresConfirmation && retryFile) {
          setPendingUpload({
            file: retryFile,
            documentTypeId: retryDocumentTypeId,
          });
        }

        // DEBUG: This is the single file upload path
        console.log(
          "🔍 USING SINGLE FILE UPLOAD PATH - This should have user choice"
        );

        // DEBUG: Log what we're about to set
        console.log("🔍 SETTING MODAL STATE:", {
          requiresConfirmation,
          hasOnProceed: !!requiresConfirmation,
          hasRetryFile: !!retryFile,
          retryDocumentTypeId,
        });
        
        setQualityErrorModal({
          isOpen: true,
          fileName,
          qualityScore: qualityReport.score,
          failureReason: mainIssue,
          recommendation: mainRecommendation,
          requiresConfirmation,
          onClose: () => {
            setQualityErrorModal((prev) => ({ ...prev, isOpen: false }));
            setPendingUpload(null);
          },
          onProceed: requiresConfirmation
            ? async () => {
                console.log("🔍 RETRY UPLOAD - Starting force upload with:", {
                  fileName: retryFile?.name,
                  documentTypeId: retryDocumentTypeId,
                });
                setQualityErrorModal((prev) => ({ ...prev, isOpen: false }));

                if (retryFile && retryDocumentTypeId) {
                  try {
                    // Retry upload with forceUpload flag - use captured values instead of state
                    const newDocument = await apiClient.uploadDocument(
                      selectedCase,
                      retryDocumentTypeId,
                      retryFile,
                      true
                    );

                    setUploadedDocuments((prev) => {
                      // Filter out any existing document with the same ID to prevent duplicates
                      const filteredPrev = prev.filter(
                        (doc) => doc.id !== newDocument.id
                      );
                      return [
                        {
                          ...newDocument,
                          documentType: retryDocumentTypeId,
                        },
                        ...filteredPrev,
                      ];
                    });

                    setSelectedCategories((prev) =>
                      prev.map((req) =>
                        req.id === retryDocumentTypeId
                          ? { ...req, status: "uploaded" }
                          : req
                      )
                    );

                    await updateDocumentRequirements();

                    setSuccessModal({
                      isOpen: true,
                      message: `Successfully uploaded ${newDocument.fileName} (quality override)`,
                      documentCount: 1,
                    });

                    setPendingUpload(null);
                  } catch (retryError) {
                    console.error("❌ Force upload failed:", retryError);
                    alert(
                      "Upload failed even with override. Please try again or contact support."
                    );
                    setPendingUpload(null);
                  }
                } else {
                  console.error(
                    "❌ Retry failed: Missing file or document type data"
                  );
                  alert(
                    "Upload retry failed due to missing data. Please try uploading the file again."
                  );
                  setPendingUpload(null);
                }
              }
            : undefined,
        });
      } else {
        // Check if it's a duplicate error
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        const isDuplicateError = errorMessage.includes(
          "Duplicate document detected"
        );

        if (isDuplicateError) {
          // Extract filename from error message for better modal display
          const fileNameMatch = errorMessage.match(/\"(.+?)\"/);
          const fileName = fileNameMatch ? fileNameMatch[1] : "Unknown file";

          setDuplicateModal({
            isOpen: true,
            duplicateFiles: [fileName],
        });
      } else {
        setSuccessModal({
          isOpen: true,
            message: `Upload failed: ${errorMessage}`,
            documentCount: 0,
        });
        }
      }
    } finally {
      setUploading(null);
    }
  };

  const handleBulkUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || !selectedCase) return;

    // Check file sizes (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const oversizedFiles = Array.from(files).filter(
      (file) => file.size > maxSize
    );
    
    if (oversizedFiles.length > 0) {
      // Show error for the first oversized file
      const firstOversizedFile = oversizedFiles[0];
      const errorMessage =
        oversizedFiles.length === 1
        ? `File "${firstOversizedFile.name}" exceeds the 10MB limit.`
        : `${oversizedFiles.length} files exceed the 10MB limit. First file: "${firstOversizedFile.name}"`;
      
      setFileSizeErrorModal({
        isOpen: true,
        fileName:
          oversizedFiles.length === 1
            ? firstOversizedFile.name
            : `${oversizedFiles.length} files`,
        fileSize: firstOversizedFile.size,
        maxSize,
      });
      event.target.value = ""; // Reset file input
      return;
    }

    // Check for duplicate filenames before processing
    const duplicateFiles = Array.from(files).filter((file) =>
      uploadedDocuments.some((doc) => doc.fileName === file.name)
    );

    if (duplicateFiles.length > 0) {
      setDuplicateModal({
        isOpen: true,
        duplicateFiles: duplicateFiles.map((f) => f.name),
      });
      event.target.value = ""; // Reset file input
      return;
    }

    // Start processing
    setProcessingStatus({
      isProcessing: true,
      progress: 0,
      processedCount: 0,
      totalCount: files.length,
      message: "Analyzing documents...",
    });

    try {
      // Categorize documents
      const categorizedDocs = Array.from(files).map((file) => {
        const { category, confidence } = categorizeDocument(file.name);
        return {
          file,
          suggestedCategory: category,
          confidence,
          finalCategory: category,
        };
      });

      // Update progress
      setProcessingStatus((prev) => ({
        ...prev,
        progress: 50,
        processedCount: files.length,
        message: "Categorization complete!",
      }));

      // Show categorization review modal
      setCategorizationModal({
        isOpen: true,
        documents: categorizedDocs,
      });
    } catch (error) {
      console.error("Categorization error:", error);
      alert("Error processing documents. Please try again.");
    } finally {
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
        processedCount: 0,
        totalCount: 0,
        message: "",
      });
      event.target.value = ""; // Reset file input
    }
  };

  const handleBulkDownload = async () => {
    if (!selectedCase || uploadedDocuments.length === 0) return;
    
    setBulkDownloading(true);
    
    try {
      console.log("Starting bulk download for case:", selectedCase);
      
      const blob = await apiClient.downloadAllDocuments(selectedCase);
      
      // Create a download link for the zip file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `case-documents-${selectedCase}.zip`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log("Bulk download completed");
    } catch (error) {
      console.error("Bulk download error:", error);
      setSuccessModal({
        isOpen: true,
        message: `Bulk download failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleDownloadDocument = async (
    documentId: string,
    fileName: string
  ) => {
    try {
      console.log("Starting download for:", fileName);
      console.log("Document ID being used:", documentId);
      console.log(
        "Full document object:",
        uploadedDocuments.find((doc) => doc.id === documentId)
      );

      // Check if this is a merged document (filename starts with "merged_")
      const isMergedDocument = fileName.startsWith("merged_");
      
      let blob;
      if (isMergedDocument) {
        // For merged documents, we need to find the correct merged document ID
        console.log("Looking for merged document with filename:", fileName);
        
        if (!selectedCase) {
          throw new Error("No case selected");
        }
        
        // Find the merged document by filename in the merged_documents table
        const { data: mergedDocs, error: fetchError } = await supabase
          .from("merged_documents")
          .select("id, file_name")
          .eq("case_id", selectedCase)
          .eq("file_name", fileName);
        
        if (fetchError) {
          console.error("Error fetching merged documents:", fetchError);
          throw new Error("Failed to find merged document");
        }
        
        if (!mergedDocs || mergedDocs.length === 0) {
          console.error("No merged document found with filename:", fileName);
          throw new Error("Merged document not found in database");
        }
        
        const mergedDocId = mergedDocs[0].id;
        console.log(
          "Found merged document ID:",
          mergedDocId,
          "for filename:",
          fileName
        );
        
        // Use the correct merged document ID for download
        blob = await apiClient.downloadMergedDocument(mergedDocId);
        console.log("Downloaded merged document:", fileName);
      } else {
        // Use regular document download method
        blob = await apiClient.downloadDocument(documentId);
        console.log("Downloaded regular document:", fileName);
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log("Download completed for:", fileName);
    } catch (error) {
      console.error("Download error:", error);
      setSuccessModal({
        isOpen: true,
        message: `Download failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    }
  };

  const handleViewDocument = async (documentId: string, fileName: string) => {
    try {
      console.log("Starting view for:", fileName);
      
      // Check if this is a merged document (filename starts with "merged_")
      const isMergedDocument = fileName.startsWith("merged_");
      
      let blob;
      if (isMergedDocument) {
        // For merged documents, we need to find the correct merged document ID
        console.log("Looking for merged document with filename:", fileName);
        
        if (!selectedCase) {
          throw new Error("No case selected");
        }
        
        // Find the merged document by filename in the merged_documents table
        const { data: mergedDocs, error: fetchError } = await supabase
          .from("merged_documents")
          .select("id, file_name")
          .eq("case_id", selectedCase)
          .eq("file_name", fileName);
        
        if (fetchError) {
          console.error("Error fetching merged documents:", fetchError);
          throw new Error("Failed to find merged document");
        }
        
        if (!mergedDocs || mergedDocs.length === 0) {
          console.error("No merged document found with filename:", fileName);
          throw new Error("Merged document not found in database");
        }
        
        const mergedDocId = mergedDocs[0].id;
        console.log(
          "Found merged document ID:",
          mergedDocId,
          "for filename:",
          fileName
        );
        
        // Use the correct merged document ID for download
        blob = await apiClient.downloadMergedDocument(mergedDocId);
        console.log("Downloaded merged document for viewing:", fileName);
      } else {
        // Use regular document download method
        blob = await apiClient.downloadDocument(documentId);
        console.log("Downloaded regular document for viewing:", fileName);
      }
      
      // Determine if the file can be viewed in browser
      const canViewInBrowser = (filename: string): boolean => {
        const extension = filename.toLowerCase().split(".").pop();
        const viewableExtensions = [
          "pdf",
          "jpg",
          "jpeg",
          "png",
          "gif",
          "bmp",
          "webp",
          "txt",
        ];
        return viewableExtensions.includes(extension || "");
      };
      
      if (canViewInBrowser(fileName)) {
        // For viewable files, open in new tab
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        console.log("View opened in browser for:", fileName);
      } else {
        // For non-viewable files, show custom modal
        setFileTypeModal({
          isOpen: true,
          documentId,
          fileName,
          blob,
        });
        console.log("Showing file type modal for:", fileName);
      }
    } catch (error) {
      console.error("View error:", error);
      setSuccessModal({
        isOpen: true,
        message: `Cannot view document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    // Find the document to get its name for the confirmation
    const documentToDelete = uploadedDocuments.find(
      (doc) => doc.id === documentId
    );
    if (!documentToDelete) {
      console.error("Document not found for deletion:", documentId);
      return;
    }

    // Open the delete confirmation modal
    setDeleteModal({
      isOpen: true,
      documentId,
      fileName: documentToDelete.fileName,
    });
  };

  const confirmDelete = async () => {
    if (!deleteModal.documentId) return;
    
    try {
      // Find the document before deleting to get its document type
      const documentToDelete = uploadedDocuments.find(
        (doc) => doc.id === deleteModal.documentId
      );
      if (!documentToDelete) {
        console.error(
          "Document not found for deletion:",
          deleteModal.documentId
        );
        return;
      }

      // Check if this is a merged document (filename starts with "merged_")
      const isMergedDocument = documentToDelete.fileName.startsWith("merged_");
      
      if (isMergedDocument) {
        // For merged documents, we need to find the correct merged document ID
        // since the main documents list uses the documents table ID
        console.log(
          "Looking for merged document with filename:",
          documentToDelete.fileName
        );
        
        // Get the current case ID
        if (!selectedCase) {
          throw new Error("No case selected");
        }
        
        // Find the merged document by filename in the merged_documents table
        const { data: mergedDocs, error: fetchError } = await supabase
          .from("merged_documents")
          .select("id, file_name")
          .eq("case_id", selectedCase)
          .eq("file_name", documentToDelete.fileName);
        
        if (fetchError) {
          console.error("Error fetching merged documents:", fetchError);
          throw new Error("Failed to find merged document");
        }
        
        if (!mergedDocs || mergedDocs.length === 0) {
          console.error(
            "No merged document found with filename:",
            documentToDelete.fileName
          );
          throw new Error("Merged document not found in database");
        }
        
        const mergedDocId = mergedDocs[0].id;
        console.log(
          "Found merged document ID:",
          mergedDocId,
          "for filename:",
          documentToDelete.fileName
        );
        
        // Use the correct merged document ID for deletion
        await apiClient.deleteMergedDocument(mergedDocId);
        console.log(
          "Merged document deleted successfully:",
          documentToDelete.fileName
        );
      } else {
        // Use regular document delete method
        await apiClient.deleteDocument(deleteModal.documentId);
        console.log(
          "Regular document deleted successfully:",
          documentToDelete.fileName
        );
      }
      
      const newUploadedDocuments = uploadedDocuments.filter(
        (doc) => doc.id !== deleteModal.documentId
      );
      setUploadedDocuments(newUploadedDocuments);
      
      // For merged documents, also refresh the main documents list
      if (isMergedDocument) {
        await fetchDocuments();
      }
      if (!isMergedDocument) {
        // Check if any other documents of the same type exist in the new list
        const remainingDocsOfType = newUploadedDocuments.some(
          (doc) => doc.documentType === documentToDelete.documentType
        );

        // If no other documents of this type remain, update the category status to 'pending'
        if (!remainingDocsOfType) {
          setSelectedCategories((prev) =>
            prev.map((req) =>
            req.id === documentToDelete.documentType 
                ? { ...req, status: "pending" }
              : req
            )
          );
        }
      }
    } catch (error) {
      console.error("Delete error:", error);
      setSuccessModal({
        isOpen: true,
        message: `Delete failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    } finally {
      // Close the modal
      setDeleteModal({ isOpen: false, documentId: null, fileName: null });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, documentId: null, fileName: null });
  };

  const handleFileTypeDownload = () => {
    if (!fileTypeModal.blob || !fileTypeModal.fileName) return;
    
    try {
      // Trigger download
      const url = window.URL.createObjectURL(fileTypeModal.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileTypeModal.fileName;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log("Download triggered for:", fileTypeModal.fileName);
    } catch (error) {
      console.error("Download error:", error);
      alert("Download failed. Please try again.");
    } finally {
      // Close modal
      setFileTypeModal({
        isOpen: false,
        documentId: null,
        fileName: null,
        blob: null,
      });
    }
  };

  const cancelFileTypeModal = () => {
    setFileTypeModal({
      isOpen: false,
      documentId: null,
      fileName: null,
      blob: null,
    });
  };

  const closeFileSizeErrorModal = () => {
    setFileSizeErrorModal({
      isOpen: false,
      fileName: "",
      fileSize: 0,
      maxSize: 2 * 1024 * 1024,
    });
  };

  const updateDocumentCategory = (index: number, newCategory: string) => {
    setCategorizationModal((prev) => ({
      ...prev,
      documents: prev.documents.map((doc, i) => 
        i === index ? { ...doc, finalCategory: newCategory } : doc
      ),
    }));
  };

  const acceptAllCategorizations = async () => {
    if (!selectedCase) return;

    setProcessingStatus({
      isProcessing: true,
      progress: 60,
      processedCount: 0,
      totalCount: categorizationModal.documents.length,
      message: "Uploading documents...",
    });

    try {
      const uploadResults: any[] = [];
      const failedUploads: Array<{
        fileName: string;
        qualityReport?: any;
        error: string;
      }> = [];
      
      for (
        let index = 0;
        index < categorizationModal.documents.length;
        index++
      ) {
        const doc = categorizationModal.documents[index];
        
        try {
          const result = await apiClient.uploadDocument(
            selectedCase,
            doc.finalCategory,
            doc.file
          );
          uploadResults.push(result);
          
          // Update progress
          setProcessingStatus((prev) => ({
            ...prev,
            progress:
              60 + ((index + 1) / categorizationModal.documents.length) * 40,
            processedCount: index + 1,
            message: `Uploading ${index + 1}/${
              categorizationModal.documents.length
            }...`,
          }));
        } catch (error) {
          console.error(`Upload failed for ${doc.file.name}:`, error);
          
          // Handle quality check errors
          if (error instanceof Error && (error as any).qualityReport) {
            const qualityReport = (error as any).qualityReport;
            failedUploads.push({
              fileName: doc.file.name,
              qualityReport,
              error: error.message,
            });
          } else {
            failedUploads.push({
              fileName: doc.file.name,
              error: error instanceof Error ? error.message : "Unknown error",
            });
          }
          
          // Update progress even for failed uploads
          setProcessingStatus((prev) => ({
            ...prev,
            progress:
              60 + ((index + 1) / categorizationModal.documents.length) * 40,
            processedCount: index + 1,
            message: `Uploading ${index + 1}/${
              categorizationModal.documents.length
            }...`,
          }));
        }
      }
      
      console.log("Upload results:", uploadResults);
      console.log(
        "Document types uploaded:",
        uploadResults.map((doc) => doc.documentType)
      );
      console.log("Failed uploads:", failedUploads);
      
      // Add successfully uploaded documents
      if (uploadResults.length > 0) {
        setUploadedDocuments((prev) => [...uploadResults, ...prev]);
      }
      
      // Automatically add categories to selected categories if they're not already there
      const uniqueCategories = Array.from(
        new Set(uploadResults.map((doc) => doc.documentType))
      );
      const newCategories = uniqueCategories.filter(
        (categoryId) =>
          !selectedCategories.some((selected) => selected.id === categoryId)
      );

      console.log("Auto-categorization debug:");
      console.log(
        "  - All document categories:",
        categorizationModal.documents.map((doc) => doc.finalCategory)
      );
      console.log("  - Unique categories:", uniqueCategories);
      console.log("  - New categories to add:", newCategories);
      console.log(
        "  - Current selected categories:",
        selectedCategories.map((cat) => cat.id)
      );
      
      if (newCategories.length > 0) {
        console.log(
          "Adding new categories to selected categories:",
          newCategories
        );
        
        const categoriesToAdd: DocumentRequirement[] = [];
        
        for (const categoryId of newCategories) {
          const category = availableCategories.find(
            (cat) => cat.id === categoryId
          );
          if (category) {
            categoriesToAdd.push({
              id: category.id,
              name: category.name,
              description: category.description,
              status: "uploaded",
              isRequired: true,
              category: category.category,
            });
          }
        }
        
        if (categoriesToAdd.length > 0) {
          setSelectedCategories((prev) => [...prev, ...categoriesToAdd]);
          
          // Save new categories to database
          try {
            // Check for existing categories in database to prevent duplicates
            const existingCategories = await supabase
              .from("selected_document_categories")
              .select("category_id")
              .eq("case_id", selectedCase)
              .in(
                "category_id",
                categoriesToAdd.map((cat) => cat.id)
              );

            const existingCategoryIds =
              existingCategories.data?.map((cat) => cat.category_id) || [];
            const categoriesToSave = categoriesToAdd.filter(
              (category) => !existingCategoryIds.includes(category.id)
            );
            
            if (categoriesToSave.length > 0) {
              const savePromises = categoriesToSave.map((category) =>
                supabase.from("selected_document_categories").insert({
                    case_id: selectedCase,
                    category_id: category.id,
                    category_name: category.name,
                    category_description: category.description,
                  category_type: "standard",
                  status: "uploaded",
                    is_required: true,
                  created_by: user?.id || "",
                  })
              );
              
              await Promise.all(savePromises);
              console.log("New categories saved to database");
            } else {
              console.log("All categories already exist in database");
            }
          } catch (dbError) {
            console.warn("Failed to save new categories to database:", dbError);
          }
        }
      }
      
      // Close modal and show success
      setCategorizationModal({ isOpen: false, documents: [] });
      
      // Update document requirements after state update
      setTimeout(() => {
        updateDocumentRequirements();
      }, 100);
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
        processedCount: 0,
        totalCount: 0,
        message: "",
      });

      // Handle upload results - support partial success

      // First, handle successful uploads
      if (uploadResults.length > 0) {
        console.log(`✅ Successfully uploaded ${uploadResults.length} files`);

        // Add successful uploads to the UI (prevent duplicates)
        const successfulDocuments = uploadResults.map((doc) => ({
          ...doc,
          documentType:
            categorizationModal.documents.find(
              (catDoc) => catDoc.file.name === doc.fileName
            )?.finalCategory || "unknown",
        }));

        setUploadedDocuments((prev) => {
          // Filter out any existing documents with the same ID to prevent duplicates
          const filteredPrev = prev.filter(
            (existingDoc) =>
              !successfulDocuments.some(
                (newDoc) => newDoc.id === existingDoc.id
              )
          );
          return [...successfulDocuments, ...filteredPrev];
        });

        // Update document requirements for successful uploads
        for (const doc of successfulDocuments) {
          setSelectedCategories((prev) =>
            prev.map((req) =>
              req.id === doc.documentType ? { ...req, status: "uploaded" } : req
            )
          );
        }

        await updateDocumentRequirements();
      }

      // Then handle failed uploads
      if (failedUploads.length > 0) {
        console.log(`⚠️ ${failedUploads.length} files failed upload`);

        // Show quality error for the first failed upload
        const firstFailed = failedUploads[0];
        if (firstFailed.qualityReport) {
          // DEBUG: This is the bulk upload path
          console.log(
            "🔍 USING BULK UPLOAD PATH - Now with user choice support!"
          );

          // Check if this should require confirmation (score < 25)
          const shouldRequireConfirmation =
            firstFailed.qualityReport.score < 25;

          // For bulk uploads with single file, allow user choice. For true bulk (multiple files), disable for now
          const isSingleFileInBulk = categorizationModal.documents.length === 1;
          const enableConfirmation =
            shouldRequireConfirmation && isSingleFileInBulk;

          // Store the failed document for potential retry (capture values to avoid race condition)
          const failedDoc = enableConfirmation
            ? categorizationModal.documents.find(
                (doc) => doc.file.name === firstFailed.fileName
              )
            : null;
          const retryFile = failedDoc?.file;
          const retryDocumentTypeId = failedDoc?.finalCategory;

          if (enableConfirmation && failedDoc) {
            setPendingUpload({
              file: failedDoc.file,
              documentTypeId: failedDoc.finalCategory,
            });
          }

          // Create a more informative message for bulk uploads
          const qualityMessage =
            uploadResults.length > 0
              ? `${uploadResults.length} files uploaded successfully. This file has quality issues:`
              : "Document quality issue detected:";

          console.log("🔍 BULK UPLOAD RETRY SETUP:", {
            enableConfirmation,
            hasRetryFile: !!retryFile,
            retryFileName: retryFile?.name,
            retryDocumentTypeId,
          });

          // Create better recommendation message for bulk uploads
          const baseRecommendation =
            firstFailed.qualityReport.recommendations[0] ||
            "Document appears to be blurry. Please re-scan with better focus.";
          const bulkRecommendation =
            uploadResults.length > 0
              ? `${baseRecommendation} If you want to proceed with uploading this file despite the quality issues, please upload it individually using the single file upload option.`
              : baseRecommendation;

          setQualityErrorModal({
            isOpen: true,
            fileName: firstFailed.fileName,
            qualityScore: firstFailed.qualityReport.score,
            failureReason:
              qualityMessage +
              " " +
              (firstFailed.qualityReport.issues[0] ||
                "Document quality is too low"),
            recommendation: bulkRecommendation,
            requiresConfirmation: enableConfirmation,
            onClose: () => {
              setQualityErrorModal((prev) => ({ ...prev, isOpen: false }));
              setPendingUpload(null);
            },
            onProceed: enableConfirmation
              ? async () => {
                  console.log(
                    "🔍 BULK RETRY UPLOAD - Starting force upload with:",
                    {
                      fileName: retryFile?.name,
                      documentTypeId: retryDocumentTypeId,
                    }
                  );
                  setQualityErrorModal((prev) => ({ ...prev, isOpen: false }));

                  if (retryFile && retryDocumentTypeId) {
                    try {
                      // Retry upload with forceUpload flag - use captured values instead of state
                      const newDocument = await apiClient.uploadDocument(
                        selectedCase,
                        retryDocumentTypeId,
                        retryFile,
                        true
                      );

                      setUploadedDocuments((prev) => {
                        // Filter out any existing document with the same ID to prevent duplicates
                        const filteredPrev = prev.filter(
                          (doc) => doc.id !== newDocument.id
                        );
                        return [
                          {
                            ...newDocument,
                            documentType: retryDocumentTypeId,
                          },
                          ...filteredPrev,
                        ];
                      });

                      setSelectedCategories((prev) =>
                        prev.map((req) =>
                          req.id === retryDocumentTypeId
                            ? { ...req, status: "uploaded" }
                            : req
                        )
                      );

                      await updateDocumentRequirements();

                      setSuccessModal({
                        isOpen: true,
                        message: `Successfully uploaded ${newDocument.fileName} (quality override)`,
                        documentCount: 1,
                      });

                      setPendingUpload(null);

                      // Close the categorization modal since upload succeeded
                      setCategorizationModal({
                        isOpen: false,
                        documents: [],
                      });
                    } catch (retryError) {
                      console.error("❌ Force upload failed:", retryError);
                      alert(
                        "Upload failed even with override. Please try again or contact support."
                      );
                      setPendingUpload(null);
                    }
        } else {
                    console.error(
                      "❌ Bulk retry failed: Missing file or document type data"
                    );
                    alert(
                      "Upload retry failed due to missing data. Please try uploading the file again."
                    );
                    setPendingUpload(null);
                  }
                }
              : undefined,
          });
        } else {
          // Non-quality error (like network error, file too large, etc.)
          setSuccessModal({
            isOpen: true,
            message: `Upload failed for ${firstFailed.fileName}: ${firstFailed.error}`,
            documentCount: 0,
          });
        }

        // If we have both successes and failures, show a comprehensive message after handling the quality error
        if (uploadResults.length > 0) {
          // Don't show the quality modal if there were successful uploads - instead show summary
          const hasQualityFailures = failedUploads.some((f) => f.qualityReport);
          const hasOtherFailures = failedUploads.some((f) => !f.qualityReport);

          let summaryMessage = `✅ Successfully uploaded ${uploadResults.length} documents! `;

          if (hasQualityFailures && hasOtherFailures) {
            summaryMessage += `⚠️ ${failedUploads.length} documents failed (quality issues and other errors).`;
          } else if (hasQualityFailures) {
            summaryMessage += `⚠️ ${failedUploads.length} document(s) failed quality checks - you can upload them individually to override.`;
          } else if (hasOtherFailures) {
            summaryMessage += `❌ ${failedUploads.length} document(s) failed due to errors.`;
          }

          // Show summary after a brief delay (after quality modal is handled)
          setTimeout(() => {
        setSuccessModal({
          isOpen: true,
              message: summaryMessage,
              documentCount: uploadResults.length,
            });
          }, 500);
        }
      } else if (uploadResults.length > 0) {
        // All successful uploads
        setSuccessModal({
          isOpen: true,
          message: `Successfully uploaded and categorized ${uploadResults.length} documents!`,
          documentCount: uploadResults.length,
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      setSuccessModal({
        isOpen: true,
        message: "Some files failed to upload. Please try again.",
        documentCount: 0,
      });
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
        processedCount: 0,
        totalCount: 0,
        message: "",
      });
    }
  };

  const closeCategorizationModal = () => {
    setCategorizationModal({ isOpen: false, documents: [] });
    setProcessingStatus({
      isProcessing: false,
      progress: 0,
      processedCount: 0,
      totalCount: 0,
      message: "",
    });
  };

  const closeSuccessModal = () => {
    setSuccessModal({
      isOpen: false,
      message: "",
      documentCount: 0,
    });
  };

  // Auto-dismiss success modal after 2 seconds (for success messages and download states, not errors)
  useEffect(() => {
    if (
      successModal.isOpen &&
      (successModal.documentCount > 0 ||
        successModal.message?.toLowerCase().includes("download"))
    ) {
      const timer = setTimeout(() => {
        closeSuccessModal();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [successModal.isOpen, successModal.documentCount, successModal.message]);

  // Add category to selected categories
  const addCategory = async () => {
    if (!selectedCategoryId || !selectedCase) return;
    
    const category = availableCategories.find(
      (cat) => cat.id === selectedCategoryId
    );
    if (!category) return;
    
    // Check if category is already selected
    const isAlreadySelected = selectedCategories.some(
      (cat) => cat.id === selectedCategoryId
    );
    if (isAlreadySelected) {
      setSuccessModal({
        isOpen: true,
        message: "This category is already selected.",
        documentCount: 0,
      });
      return;
    }
    
    try {
      // Save to database using API
      await apiClient.addSelectedCategory(selectedCase, {
        category_id: category.id,
        category_name: category.name,
        category_description: category.description,
        category_icon: category.icon,
        category_type: "standard",
        created_by: user?.id || "",
      });
      
      // Add category with pending status
      const newCategory: DocumentRequirement = {
        id: category.id,
        name: category.name,
        description: category.description,
        status: "pending",
        isRequired: true,
        category: category.category,
      };
      
      setSelectedCategories((prev) => [...prev, newCategory]);
      setSelectedCategoryId(""); // Reset selection
      
      console.log("Category added to database:", category.name);
    } catch (error) {
      console.error("Error adding category:", error);
      setSuccessModal({
        isOpen: true,
        message: "Failed to add category. Please try again.",
        documentCount: 0,
      });
    }
  };

  // Remove category from selected categories
  const removeCategory = async (categoryId: string) => {
    // Find the category to check if it has uploaded documents
    const category = selectedCategories.find((cat) => cat.id === categoryId);
    
    // Prevent removal if category has uploaded documents
    if (category && category.status === "uploaded") {
      setSuccessModal({
        isOpen: true,
        message: `Cannot remove "${category.name}" category. Please delete the uploaded documents first.`,
        documentCount: 0,
      });
      return;
    }
    
    try {
      console.log("Attempting to remove category:", categoryId);
      console.log("API call to:", `/api/selected-categories/${categoryId}`);
      
      // Try API first
      try {
        const result = await apiClient.removeSelectedCategory(categoryId);
        console.log("API response:", result);
      } catch (apiError) {
        console.warn(
          "API failed, falling back to direct Supabase call:",
          apiError
        );
        
        // Fallback to direct Supabase call
        const { error } = await supabase
          .from("selected_document_categories")
          .delete()
          .eq("category_id", categoryId)
          .eq("case_id", selectedCase);
        
        if (error) {
          console.error("Supabase fallback error:", error);
          throw new Error(`Database error: ${error.message}`);
        }
        
        console.log("Category removed via Supabase fallback");
      }
      
      // Remove from state
      setSelectedCategories((prev) =>
        prev.filter((cat) => cat.id !== categoryId)
      );
      
      console.log("Category removed from database:", category?.name);
    } catch (error) {
      console.error("Error removing category:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      setSuccessModal({
        isOpen: true,
        message: `Failed to remove category: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    }
  };

  // Open custom category modal
  const openCustomCategoryModal = () => {
    setCustomCategoryModal({
      isOpen: true,
      name: "",
      description: "",
    });
  };

  // Close custom category modal
  const closeCustomCategoryModal = () => {
    setCustomCategoryModal({
      isOpen: false,
      name: "",
      description: "",
    });
  };

  // Create custom category
  const createCustomCategory = async () => {
    try {
      if (!customCategoryModal.name.trim() || !selectedCase) {
        return;
      }

      setLoading(true);

      // Add to selected categories with custom type
      const { error: apiError } = await apiClient.addSelectedCategory(
        selectedCase,
        {
          category_id: customCategoryModal.name
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_"),
        category_name: customCategoryModal.name.trim(),
          category_description:
            customCategoryModal.description.trim() ||
            "Custom document category",
          category_type: "custom",
          created_by: user?.id || "",
        }
      );

      if (apiError) {
        console.error("API Error adding custom category:", apiError);
        
        // Fallback to direct Supabase call
        const { error: supabaseError } = await supabase
          .from("selected_document_categories")
          .insert({
            case_id: selectedCase,
            category_id: customCategoryModal.name
              .trim()
              .toLowerCase()
              .replace(/\s+/g, "_"),
            category_name: customCategoryModal.name.trim(),
            category_description:
              customCategoryModal.description.trim() ||
              "Custom document category",
            category_type: "custom",
            is_required: false,
          });

        if (supabaseError) {
          console.error(
            "Supabase Error adding custom category:",
            supabaseError
          );
          setSuccessModal({
            isOpen: true,
            message: "Failed to add custom category. Please try again.",
            documentCount: 0,
          });
          return;
        }
      }

      // Refresh selected categories
      await fetchSelectedCategories();
      closeCustomCategoryModal();
      console.log(
        "Custom category added to database:",
        customCategoryModal.name
      );
      
      setSuccessModal({
        isOpen: true,
        message: `Custom category "${customCategoryModal.name}" added successfully!`,
        documentCount: 1,
      });
    } catch (error) {
      console.error("Error creating custom category:", error);
      setSuccessModal({
        isOpen: true,
        message: "Failed to create custom category. Please try again.",
        documentCount: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // PDF Merging Functions
  const loadMergedDocuments = async (categoryId: string) => {
    try {
      if (!selectedCase) return;
      
      const merged = await apiClient.getMergedDocuments(
        selectedCase,
        categoryId
      );
      setMergedDocuments(merged.data || []);
      console.log("Loaded merged documents for category:", categoryId, merged);
    } catch (error) {
      console.error("Error loading merged documents:", error);
    }
  };

  const handleMergePDFs = async (categoryId: string) => {
    if (selectedDocumentsForMerge.length < 2) {
      setSuccessModal({
        isOpen: true,
        message: "Please select at least 2 documents to merge",
        documentCount: 0,
      });
      return;
    }

    // Prevent multiple simultaneous merge operations
    if (merging) {
      console.log("⚠️ Merge already in progress, ignoring duplicate call");
      return;
    }

    try {
      setMerging(true);
      console.log("Starting merge operation for category:", categoryId);
      setSuccessModal({
        isOpen: true,
        message: "Merging PDFs...",
        documentCount: 0,
      });

      // For cross-category merges, use a special category ID
      const actualCategoryId =
        categoryId === "all_categories" ? "cross_category_merge" : categoryId;

      const mergeData: any = {
        compressionSizeMB: compressionSize,
        customFileName: customFileName ? customFileName.trim() : undefined,
        caseId: selectedCase,
        categoryId: actualCategoryId,
        documentIds: selectedDocumentsForMerge,
        mergeOrder,
      };

      // Add customOrder only when mergeOrder is 'custom'
      if (mergeOrder === "custom") {
        mergeData.customOrder = selectedDocumentsForMerge;
      }

      const result = await apiClient.mergePDFDocuments(mergeData);

      console.log("Merge result:", result);
      
      // Reload merged documents in the modal
      await loadMergedDocuments(actualCategoryId);
      
      // Refresh the main uploaded documents list to show the merged PDF
      await fetchDocuments();
      
      // Clear selection
      setSelectedDocumentsForMerge([]);
      setMergeModal({ isOpen: false, categoryId: "" });

      // Show success message with the actual merged filename from backend
      const mergedFileName =
        result.data.mergedFileName || "merged_document.pdf";
      
      setSuccessModal({
        isOpen: true,
        message: `Successfully merged ${result.data.mergedDocuments.length} documents into ${mergedFileName}`,
        documentCount: result.data.mergedDocuments.length,
      });
    } catch (error) {
      console.error("Error merging PDFs:", error);
      setSuccessModal({
        isOpen: true,
        message: `Failed to merge PDFs: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    } finally {
      setMerging(false);
    }
  };

  const handleDownloadMerged = async (mergedDocumentId: string) => {
    try {
      setSuccessModal({
        isOpen: true,
        message: "Downloading merged PDF...",
        documentCount: 0,
      });
      
      const blob = await apiClient.downloadMergedDocument(mergedDocumentId);
      
      // Find the merged document and generate filename based on category
      const mergedDoc = mergedDocuments.find(
        (doc) => doc.id === mergedDocumentId
      );
      const categoryId = mergedDoc?.categoryId || "unknown";
      const timestamp = new Date().toISOString().split("T")[0];
      const downloadFileName = `merged_${categoryId}_${timestamp}.pdf`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccessModal({
        isOpen: true,
        message: "Download completed",
        documentCount: 1,
      });
    } catch (error) {
      console.error("Error downloading merged PDF:", error);
      setSuccessModal({
        isOpen: true,
        message: `Failed to download: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    }
  };

  const handleDeleteMerged = async (mergedDocumentId: string) => {
    try {
      console.log(
        "Frontend: Deleting merged document with ID:",
        mergedDocumentId
      );
      await apiClient.deleteMergedDocument(mergedDocumentId);
      console.log("✅ Frontend: Merged document deleted successfully");
      
      // Reload merged documents for the current category
      if (mergeModal.categoryId) {
        await loadMergedDocuments(mergeModal.categoryId);
      }
      
      // Also refresh the main documents list to remove the deleted merged document
      await fetchDocuments();
      
      setSuccessModal({
        isOpen: true,
        message: "Merged document deleted successfully",
        documentCount: 1,
      });
    } catch (error) {
      console.error("❌ Error deleting merged document:", error);
      setSuccessModal({
        isOpen: true,
        message: `Failed to delete: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocumentsForDelete.length === 0) {
      setSuccessModal({
        isOpen: true,
        message: "Please select at least one document to delete",
        documentCount: 0,
      });
      return;
    }

    // Prepare documents for confirmation modal
    const documentsToDelete = selectedDocumentsForDelete.map((id) => {
      const doc = uploadedDocuments.find((d) => d.id === id);
      return {
        id,
        fileName: doc?.fileName || "Unknown Document",
        documentType: doc?.documentType || "Unknown Type",
      };
    });

    // Show confirmation modal
    setBulkDeleteModal({
      isOpen: true,
      documents: documentsToDelete,
    });
  };

  const confirmBulkDelete = async () => {
    try {
      setDeleting(true);
      setBulkDeleteModal({ isOpen: false, documents: [] });
      
      // Initialize deletion progress modal
      const totalToDelete = selectedDocumentsForDelete.length;
      setDeletionProgress({
        isOpen: true,
        isDeleting: true,
        totalDocuments: totalToDelete,
        deletedCount: 0,
        currentFileName: undefined,
      });

      let deletedCount = 0;
      const errors: string[] = [];

      // Delete all selected documents one by one to handle errors properly
      for (const documentId of selectedDocumentsForDelete) {
        try {
          const document = uploadedDocuments.find(
            (doc) => doc.id === documentId
          );
          if (!document) {
            errors.push(`Document with ID ${documentId} not found`);
            continue;
          }

          // Update progress with current file
          setDeletionProgress((prev) => ({
            ...prev,
            currentFileName: document.fileName,
          }));

          // Check if this is a merged document
          const isMergedDocument =
            document.fileName.startsWith("merged_") ||
            document.fileName.startsWith("Combined_") ||
            document.documentType === "merged_document";
          
          if (isMergedDocument) {
            // For merged documents, we need to find the correct merged document ID
            if (!selectedCase) {
              throw new Error("No case selected");
            }
            
            // Try to find the merged document by filename in the merged_documents table
            const { data: mergedDocs, error: fetchError } = await supabase
              .from("merged_documents")
              .select("id, file_name, category_id")
              .eq("case_id", selectedCase);
            
            if (fetchError) {
              console.error("Error fetching merged documents:", fetchError);
              errors.push(
                `Failed to fetch merged documents: ${fetchError.message}`
              );
              continue;
            }
            
            console.log(
              "🔍 Looking for merged document with filename:",
              document.fileName
            );
            console.log("📄 Available merged documents:", mergedDocs);
            
            // Try multiple matching strategies
            let mergedDoc = null;
            
            // Strategy 1: Use original filename if available (for merged documents)
            const originalFileName = (document as any).originalFileName;
            if (originalFileName) {
              mergedDoc = mergedDocs?.find(
                (doc) =>
                  doc.file_name &&
                  doc.file_name.toLowerCase() === originalFileName.toLowerCase()
              );
            }
            
            // Strategy 2: Exact filename match (case-insensitive)
            if (!mergedDoc) {
              mergedDoc = mergedDocs?.find(
                (doc) =>
                  doc.file_name &&
                  doc.file_name.toLowerCase() ===
                    document.fileName.toLowerCase()
              );
            }
            
            // Strategy 3: Partial filename match (for cases where UI shows different name)
            if (!mergedDoc) {
              mergedDoc = mergedDocs?.find(
                (doc) =>
                  doc.file_name &&
                  (doc.file_name
                    .toLowerCase()
                    .includes(document.fileName.toLowerCase()) ||
                    document.fileName
                      .toLowerCase()
                      .includes(doc.file_name.toLowerCase()))
              );
            }
            
            // Strategy 3: Match by category and date pattern
            if (!mergedDoc) {
              const datePattern = document.fileName.match(/\d{4}-\d{2}-\d{2}/);
              if (datePattern) {
                mergedDoc = mergedDocs?.find(
                  (doc) =>
                  doc.file_name && doc.file_name.includes(datePattern[0])
                );
              }
            }
            
            // Strategy 4: Match any merged document if we have multiple with same category
            if (!mergedDoc && mergedDocs && mergedDocs.length > 0) {
              // If we have merged documents but can't find exact match, use the first one
              // This is a fallback for cases where the UI filename doesn't match DB filename
              mergedDoc = mergedDocs[0];
              console.log("⚠️ Using fallback merged document:", mergedDoc);
            }
            
            // Strategy 5: If the document ID itself is a merged document ID, use it directly
            if (!mergedDoc) {
              const directMergedDoc = mergedDocs?.find(
                (doc) => doc.id === documentId
              );
              if (directMergedDoc) {
                mergedDoc = directMergedDoc;
                console.log(
                  "✅ Found merged document by direct ID match:",
                  mergedDoc
                );
              }
            }
            
            if (!mergedDoc) {
              console.error(
                "❌ No merged document found with filename:",
                document.fileName
              );
              console.error(
                "❌ Available merged documents:",
                mergedDocs?.map((d) => d.file_name)
              );
              
              // Since this document doesn't exist in the database, we should remove it from the UI
              // This handles cases where the UI shows documents that were deleted from the database
              console.log(
                "🧹 Cleaning up non-existent merged document from UI:",
                document.fileName
              );
              
              // Remove this document from the uploadedDocuments list
              removeDocumentFromUI(documentId, document.fileName);
              
              // Add a success message for the cleanup
              deletedCount++;

              // Update progress count
              setDeletionProgress((prev) => ({
                ...prev,
                deletedCount: deletedCount,
              }));
              continue;
            }
            
            await apiClient.deleteMergedDocument(mergedDoc.id);
            console.log("✅ Deleted merged document:", document.fileName);
          } else {
            // Use regular document delete method
            await apiClient.deleteDocument(documentId);
            console.log("✅ Deleted regular document:", document.fileName);
          }
          
          deletedCount++;

          // Update progress count
          setDeletionProgress((prev) => ({
            ...prev,
            deletedCount: deletedCount,
          }));
        } catch (error) {
          const document = uploadedDocuments.find(
            (doc) => doc.id === documentId
          );
          const fileName = document?.fileName || documentId;
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error";

          // If it's a "document not found" error, remove it from UI but don't count as an error
          if (
            errorMessage.includes("not found") ||
            errorMessage.includes("Failed to fetch document")
          ) {
            console.log(
              `🧹 Document ${fileName} doesn't exist in database, removing from UI`
            );
            // Remove this document from the uploadedDocuments list
            removeDocumentFromUI(documentId, fileName);
            deletedCount++; // Count as successful cleanup

            // Update progress count
            setDeletionProgress((prev) => ({
              ...prev,
              deletedCount: deletedCount,
            }));
          } else {
            errors.push(`Failed to delete "${fileName}": ${errorMessage}`);
          }
        }
      }
      
      // Clear selection first
      setSelectedDocumentsForDelete([]);
      
      // Update UI state immediately to reflect deletions
      setUploadedDocuments((prev) => {
        const remainingDocs = prev.filter(
          (doc) => !selectedDocumentsForDelete.includes(doc.id)
        );
        console.log("📄 Updated UI state after deletion:", {
          previousCount: prev.length,
          newCount: remainingDocs.length,
          deletedCount: prev.length - remainingDocs.length,
        });
        return remainingDocs;
      });
      
      // Refresh the documents list from database to ensure consistency
      await fetchDocuments();
      
      // Force a second refresh after a short delay to catch any remaining stale data
      setTimeout(async () => {
        console.log("🔄 Force refreshing documents after deletion...");
        await fetchDocuments();
      }, 500);

      // Mark deletion as complete
      setDeletionProgress((prev) => ({
        ...prev,
        isDeleting: false,
        currentFileName: undefined,
      }));

      // If there were errors, show them in the old success modal after a delay
      if (errors.length > 0) {
        setTimeout(() => {
          if (deletedCount > 0) {
        // Some documents deleted, some failed
        setSuccessModal({
          isOpen: true,
              message: `Partially successful: Deleted ${deletedCount} document${
                deletedCount > 1 ? "s" : ""
              }, but ${errors.length} failed. ${errors.join("; ")}`,
              documentCount: 0,
        });
      } else {
        // All documents failed to delete
        setSuccessModal({
          isOpen: true,
              message: `Failed to delete documents: ${errors.join("; ")}`,
              documentCount: 0,
        });
      }
        }, 2000); // Show errors after 2 seconds
      }
    } catch (error) {
      console.error("❌ Error in bulk delete:", error);

      // Mark deletion as complete with error
      setDeletionProgress((prev) => ({
        ...prev,
        isDeleting: false,
        currentFileName: undefined,
      }));

      // Show error message after a delay
      setTimeout(() => {
      setSuccessModal({
        isOpen: true,
          message: `Failed to delete documents: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          documentCount: 0,
        });
      }, 1000);
    } finally {
      setDeleting(false);
    }
  };

  const cancelBulkDelete = () => {
    setBulkDeleteModal({ isOpen: false, documents: [] });
  };

  // Function to manually remove a document from UI state
  const removeDocumentFromUI = (documentId: string, fileName: string) => {
    console.log("🗑️ Manually removing document from UI:", fileName);
    setUploadedDocuments((prev) => {
      const updated = prev.filter((doc) => doc.id !== documentId);
      console.log("📄 UI state updated:", {
        previousCount: prev.length,
        newCount: updated.length,
        removedDocument: fileName,
      });
      return updated;
    });
  };

  // Function to force cleanup of problematic merged documents
  const forceCleanupMergedDocuments = async () => {
    if (!selectedCase) return;
    
    try {
      console.log("🧹 Force cleaning up merged documents...");
      
      // First, check if there are any merged documents in the main documents table
      const { data: mainTableDocs, error: mainTableError } = await supabase
        .from("documents")
        .select("id, name, document_type")
        .eq("case_id", selectedCase);

      if (mainTableError) {
        console.error(
          "❌ Error fetching documents from main table:",
          mainTableError
        );
        return;
      }

      // Find documents in main table that look like merged documents
      const mergedDocsInMainTable = mainTableDocs.filter(
        (doc) =>
          doc.name.startsWith("merged_") ||
          doc.name.startsWith("Combined_") ||
          doc.document_type === "merged_document"
      );

      console.log(
        "🔍 Found merged documents in main table:",
        mergedDocsInMainTable
      );

      // Delete these from the main table
      for (const doc of mergedDocsInMainTable) {
        const { error: deleteError } = await supabase
          .from("documents")
          .delete()
          .eq("id", doc.id);

        if (deleteError) {
          console.error(`❌ Error deleting document ${doc.name}:`, deleteError);
        } else {
          console.log(
            `✅ Deleted merged document from main table: ${doc.name}`
          );
        }
      }
      
      // Get all merged documents from merged_documents table
      const { data: mergedDocs, error } = await supabase
        .from("merged_documents")
        .select("id, file_name, category_id")
        .eq("case_id", selectedCase);
      
      if (error) {
        console.error("❌ Error fetching merged documents for cleanup:", error);
        return;
      }
      
      console.log("📄 Current merged documents in database:", mergedDocs);
      
      // Get current UI state
      const currentUIState = uploadedDocuments.filter(
        (doc) =>
          doc.fileName.startsWith("merged_") ||
          doc.fileName.startsWith("Combined_") ||
          doc.documentType === "merged_document"
      );

      console.log("📄 Current merged documents in UI:", currentUIState);
      
      // Find documents in UI that don't exist in database
      const staleDocuments = currentUIState.filter(
        (uiDoc) => !mergedDocs?.some((dbDoc) => dbDoc.id === uiDoc.id)
      );
      
      console.log("🧹 Stale merged documents to remove:", staleDocuments);
      
      // Remove stale documents from UI
      if (staleDocuments.length > 0) {
        setUploadedDocuments((prev) => {
          const updated = prev.filter(
            (doc) => !staleDocuments.some((stale) => stale.id === doc.id)
          );
          console.log("✅ Cleaned up stale merged documents:", {
            previousCount: prev.length,
            newCount: updated.length,
            removedCount: staleDocuments.length,
          });
          return updated;
        });
      }

      // Refresh the documents list
      await fetchDocuments();
    } catch (error) {
      console.error("❌ Error in force cleanup:", error);
    }
  };

  // Drag and drop handlers for upload areas
  const handleUploadDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleUploadDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleUploadDrop = (e: React.DragEvent, documentTypeId: string) => {
    e.preventDefault();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      console.log(
        "📁 Files dropped for category:",
        documentTypeId,
        "Files:",
        Array.from(files).map((f) => f.name)
      );
      handleFileUpload(documentTypeId, files);
    }
  };

  const handleBulkUploadDrop = (e: React.DragEvent) => {
    e.preventDefault();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      console.log(
        "📁 Files dropped for bulk upload. Files:",
        Array.from(files).map((f) => f.name)
      );
      
      // Create a synthetic event for bulk upload
      const syntheticEvent = {
        target: {
          files: files,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleBulkUpload(syntheticEvent);
    }
  };

  const handleBulkDownloadSelected = async () => {
    if (selectedDocumentsForDownload.length === 0) {
      setSuccessModal({
        isOpen: true,
        message: "Download: Please select at least one document to download",
        documentCount: 0,
      });
      return;
    }

    try {
      setDownloading(true);
      setSuccessModal({
        isOpen: true,
        message: "Download in progress...",
        documentCount: 0,
      });

      let downloadedCount = 0;
      const errors: string[] = [];

      // Download all selected documents one by one
      for (const documentId of selectedDocumentsForDownload) {
        try {
          const document = uploadedDocuments.find(
            (doc) => doc.id === documentId
          );
          if (!document) {
            errors.push(`Document with ID ${documentId} not found`);
            continue;
          }

          // Check if this is a merged document
          const isMergedDocument =
            document.fileName.startsWith("merged_") ||
            document.fileName.startsWith("Combined_") ||
            document.documentType === "merged_document";
          
          let blob;
          if (isMergedDocument) {
            // For merged documents, we need to find the correct merged document ID
            if (!selectedCase) {
              throw new Error("No case selected");
            }
            
            // Try to find the merged document by filename in the merged_documents table
            const { data: mergedDocs, error: fetchError } = await supabase
              .from("merged_documents")
              .select("id, file_name, category_id")
              .eq("case_id", selectedCase);
            
            if (fetchError) {
              console.error("Error fetching merged documents:", fetchError);
              errors.push(
                `Failed to fetch merged documents: ${fetchError.message}`
              );
              continue;
            }
            
            console.log(
              "🔍 Looking for merged document with filename:",
              document.fileName
            );
            console.log("📄 Available merged documents:", mergedDocs);
            
            // Try multiple matching strategies
            let mergedDoc = null;
            
            // Strategy 1: Use original filename if available (for merged documents)
            const originalFileName = (document as any).originalFileName;
            if (originalFileName) {
              mergedDoc = mergedDocs?.find(
                (doc) =>
                  doc.file_name &&
                  doc.file_name.toLowerCase() === originalFileName.toLowerCase()
              );
            }
            
            // Strategy 2: Exact filename match (case-insensitive)
            if (!mergedDoc) {
              mergedDoc = mergedDocs?.find(
                (doc) =>
                  doc.file_name &&
                  doc.file_name.toLowerCase() ===
                    document.fileName.toLowerCase()
              );
            }
            
            // Strategy 3: Partial filename match (for cases where UI shows different name)
            if (!mergedDoc) {
              mergedDoc = mergedDocs?.find(
                (doc) =>
                  doc.file_name &&
                  (doc.file_name
                    .toLowerCase()
                    .includes(document.fileName.toLowerCase()) ||
                    document.fileName
                      .toLowerCase()
                      .includes(doc.file_name.toLowerCase()))
              );
            }
            
            // Strategy 4: Match by category and date pattern
            if (!mergedDoc) {
              const datePattern = document.fileName.match(/\d{4}-\d{2}-\d{2}/);
              if (datePattern) {
                mergedDoc = mergedDocs?.find(
                  (doc) =>
                  doc.file_name && doc.file_name.includes(datePattern[0])
                );
              }
            }
            
            // Strategy 5: Match any merged document if we have multiple with same category
            if (!mergedDoc && mergedDocs && mergedDocs.length > 0) {
              // If we have merged documents but can't find exact match, use the first one
              // This is a fallback for cases where the UI filename doesn't match DB filename
              mergedDoc = mergedDocs[0];
              console.log("⚠️ Using fallback merged document:", mergedDoc);
            }
            
            if (!mergedDoc) {
              console.error(
                "❌ No merged document found with filename:",
                document.fileName
              );
              console.error(
                "❌ Available merged documents:",
                mergedDocs?.map((d) => d.file_name)
              );
              
              // Since this document doesn't exist in the database, we should remove it from the UI
              // This handles cases where the UI shows documents that were deleted from the database
              console.log(
                "🧹 Cleaning up non-existent merged document from UI:",
                document.fileName
              );
              
              // Remove this document from the uploadedDocuments list
              setUploadedDocuments((prev) =>
                prev.filter((doc) => doc.id !== documentId)
              );
              
              // Add an error message for the cleanup
              errors.push(
                `Document "${document.fileName}" was removed from the list as it no longer exists in the database`
              );
              continue;
            }
            
            blob = await apiClient.downloadMergedDocument(mergedDoc.id);
            console.log("✅ Downloaded merged document:", document.fileName);
          } else {
            // Use regular document download method
            blob = await apiClient.downloadDocument(documentId);
            console.log("✅ Downloaded regular document:", document.fileName);
          }
          
          // Create download link for this document
          const url = window.URL.createObjectURL(blob);
          const link = window.document.createElement("a");
          link.href = url;
          link.download = document.fileName;
          
          // Trigger download
          window.document.body.appendChild(link);
          link.click();
          
          // Cleanup
          window.document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          downloadedCount++;
          
          // Small delay to prevent browser from blocking multiple downloads
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          const document = uploadedDocuments.find(
            (doc) => doc.id === documentId
          );
          const fileName = document?.fileName || documentId;
          errors.push(
            `Failed to download "${fileName}": ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          );
        }
      }
      
      // Clear selection
      setSelectedDocumentsForDownload([]);

      if (errors.length === 0) {
        // All documents downloaded successfully
        setSuccessModal({
          isOpen: true,
          message: `Download completed: ${downloadedCount} document${
            downloadedCount > 1 ? "s" : ""
          } downloaded successfully`,
          documentCount: downloadedCount,
        });
      } else if (downloadedCount > 0) {
        // Some documents downloaded, some failed
        setSuccessModal({
          isOpen: true,
          message: `Download partially successful: ${downloadedCount} document${
            downloadedCount > 1 ? "s" : ""
          } downloaded, but ${errors.length} failed. ${errors.join("; ")}`,
          documentCount: 0,
        });
      } else {
        // All documents failed to download
        setSuccessModal({
          isOpen: true,
          message: `Download failed: ${errors.join("; ")}`,
          documentCount: 0,
        });
      }
    } catch (error) {
      console.error("❌ Error in bulk download:", error);
      setSuccessModal({
        isOpen: true,
        message: `Failed to download documents: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        documentCount: 0,
      });
    } finally {
      setDownloading(false);
    }
  };

  const openMergeModal = (categoryId: string) => {
    setMergeModal({ isOpen: true, categoryId });
    loadMergedDocuments(categoryId);
  };

  const closeMergeModal = () => {
    setMergeModal({ isOpen: false, categoryId: "" });
    setSelectedDocumentsForMerge([]);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Drag and drop handlers for custom order
  const handleDragStart = (e: React.DragEvent, docId: string) => {
    setDraggedItem(docId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, docId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedItem && draggedItem !== docId) {
      setDragOverItem(docId);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetDocId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetDocId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder the selected documents
    const newOrder = [...selectedDocumentsForMerge];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetDocId);
    
    // Remove the dragged item
    newOrder.splice(draggedIndex, 1);
    
    // Insert at the target position
    newOrder.splice(targetIndex, 0, draggedItem);
    
    setSelectedDocumentsForMerge(newOrder);
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Rule-based categorization function
  const categorizeDocument = (
    fileName: string,
    fileContent?: string
  ): { category: string; confidence: number } => {
    const lowerFileName = fileName.toLowerCase();
    const lowerContent = fileContent?.toLowerCase() || "";
    
    console.log("🔍 Categorizing file:", fileName);
    
    // Enhanced categorization patterns - using exact requirement IDs
    const patterns: {
      [key: string]: { keywords: string[]; confidence: number };
    } = {
      passport: {
        keywords: ["passport", "pass_", "travel_doc", "identity"],
        confidence: 0.9,
      },
      birth_certificate: {
        keywords: [
          "birth",
          "birth_cert",
          "birth_certificate",
          "birthcert",
          "birth_certificate",
        ],
        confidence: 0.85,
      },
      marriage_certificate: {
        keywords: [
          "marriage",
          "marriage_cert",
          "marriage_certificate",
          "wedding",
          "spouse",
          "marital",
        ],
        confidence: 0.9,
      },
      employment_letter: {
        keywords: [
          "employment",
          "employ",
          "job_letter",
          "work_letter",
          "offer_letter",
          "contract",
          "position",
        ],
        confidence: 0.8,
      },
      bank_statements: {
        keywords: [
          "bank",
          "bank_statement",
          "banking",
          "account_statement",
          "financial",
          "statement",
          "bankstatement",
        ],
        confidence: 0.85,
      },
      tax_returns: {
        keywords: [
          "tax",
          "tax_return",
          "irs",
          "1040",
          "w2",
          "return",
          "income_tax",
          "tax returns",
          "taxreturns",
        ],
        confidence: 0.9,
      },
      educational_credentials: {
        keywords: [
          "education",
          "educational",
          "credential",
          "diploma",
          "degree",
          "certificate",
          "transcript",
          "academic",
          "school",
          "university",
          "college",
        ],
        confidence: 0.85,
      },
      medical_records: {
        keywords: [
          "medical",
          "health",
          "doctor",
          "hospital",
          "exam",
          "medical_report",
          "health_record",
        ],
        confidence: 0.8,
      },
      police_clearance: {
        keywords: [
          "police",
          "clearance",
          "criminal",
          "background",
          "fbi",
          "police_clearance",
        ],
        confidence: 0.85,
      },
    };

    // Check filename patterns
    for (const [category, pattern] of Object.entries(patterns)) {
      if (pattern.keywords.some((keyword) => lowerFileName.includes(keyword))) {
        console.log(
          `✅ Categorized "${fileName}" as "${category}" with confidence ${pattern.confidence}`
        );
        return { category, confidence: pattern.confidence };
      }
    }

    // Check content patterns if available
    if (fileContent) {
      for (const [category, pattern] of Object.entries(patterns)) {
        const contentMatches = pattern.keywords.filter((keyword) =>
          lowerContent.includes(keyword)
        );
        if (contentMatches.length > 0) {
          // Higher confidence for content matches
          return {
            category,
            confidence: Math.min(0.95, pattern.confidence + 0.1),
          };
        }
      }
    }

    // Default fallback
    console.log(`❌ No category found for "${fileName}", using 'other'`);
    return { category: "other", confidence: 0.3 };
  };

  const getDocumentTypeFromFilename = (filename: string): string | null => {
    const lowerFilename = filename.toLowerCase();
    
    const patterns: { [key: string]: string[] } = {
      passport: ["passport", "passport_", "pass_"],
      birth_certificate: [
        "birth",
        "birth_cert",
        "birth_certificate",
        "birthcert",
      ],
      marriage_certificate: [
        "marriage",
        "marriage_cert",
        "marriage_certificate",
        "wedding",
      ],
      employment_letter: [
        "employment",
        "employ",
        "job_letter",
        "work_letter",
        "offer_letter",
      ],
      bank_statements: [
        "bank",
        "bank_statement",
        "banking",
        "account_statement",
      ],
      tax_returns: ["tax", "tax_return", "irs", "1040", "w2"],
      educational_credentials: [
        "education",
        "educational",
        "credential",
        "diploma",
        "degree",
        "certificate",
        "transcript",
        "academic",
        "school",
        "university",
        "college",
      ],
      medical_records: ["medical", "health", "doctor", "hospital", "exam"],
      police_clearance: [
        "police",
        "clearance",
        "criminal",
        "background",
        "fbi",
      ],
    };

    for (const [docType, patternList] of Object.entries(patterns)) {
      if (patternList.some((pattern) => lowerFilename.includes(pattern))) {
        return docType;
      }
    }
    
    return null;
  };

  const canViewInBrowser = (filename: string): boolean => {
    const extension = filename.toLowerCase().split(".").pop();
    const viewableExtensions = [
      "pdf",
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "webp",
      "txt",
    ];
    return viewableExtensions.includes(extension || "");
  };

  const getViewButtonText = (filename: string): string => {
    return canViewInBrowser(filename) ? "👁️ View" : "📄 Open";
  };

  const getViewButtonTitle = (filename: string): string => {
    return canViewInBrowser(filename) 
      ? "View document in browser"
      : "Open document (will download if not viewable)";
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      personal: "👤",
      employment: "💼",
      financial: "💰",
      medical: "🏥",
      legal: "⚖️",
    };
    return icons[category] || "📄";
  };

  const getStatusIcon = (status: string) => {
    return status === "uploaded" ? "✅" : "⭕";
  };

  const getStatusColor = (status: string) => {
    return status === "uploaded" ? "text-green-600" : "text-orange-600";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - new Date(date).getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return "Uploaded today";
    if (diffDays === 2) return "Uploaded yesterday";
    if (diffDays <= 7) return `Uploaded ${diffDays - 1} days ago`;
    return `Uploaded ${diffDays - 1} days ago`;
  };

  // Debug function to force clear all caches and refresh
  const forceClearAndRefresh = () => {
    console.log("🧹 Force clearing all caches and refreshing...");
    setUploadedDocuments([]);
    setMergedDocuments([]);
    setSelectedCategories([]);
    setLoading(false);
    
    // Clear any localStorage caches
    if (selectedCase) {
      localStorage.removeItem(`documents:${selectedCase}`);
      localStorage.removeItem(`categories:${selectedCase}`);
    }
    
    // Force refresh after a short delay
    setTimeout(() => {
      fetchDocuments(true); // Force refresh with direct database query
      fetchSelectedCategories();
    }, 200);
  };

  // Expose debug function globally for console access
  React.useEffect(() => {
    (window as any).debugDocuments = {
      forceRefresh: forceClearAndRefresh,
      checkDatabase: async () => {
        if (!selectedCase) {
          console.log("❌ No case selected");
          return;
        }
        console.log("🔍 Checking database directly for case:", selectedCase);
        const { data, error } = await supabase
          .from("documents")
          .select("*")
          .eq("case_id", selectedCase);
        
        if (error) {
          console.error("❌ Database query error:", error);
        } else {
          console.log("📄 Documents in database:", data);
          console.log("📄 Documents in UI state:", uploadedDocuments);
        }
      },
    };
  }, [selectedCase, uploadedDocuments]);

  const filteredDocuments = uploadedDocuments.filter((doc) => {
    const matchesSearch = doc.fileName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All Categories" ||
      doc.documentType === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    "All Categories",
    ...Array.from(new Set(uploadedDocuments.map((doc) => doc.documentType))),
  ];

  if (loading) {
    return (
      <div className="documents-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <div
        className="documents-header"
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(139, 92, 246, 0.2)",
          borderRadius: "16px",
          padding: "20px 24px",
          marginBottom: "24px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1
          style={{
            fontSize: "28px",
            margin: "0",
            color: "#000000",
            fontWeight: "700",
          }}
        >
          Documents - Case
        </h1>
        
        <div className="case-selector">
          <select 
            value={selectedCase || ""}
            onChange={(e) => setSelectedCase(e.target.value)}
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
              borderRadius: "12px",
              padding: "10px 16px",
              fontSize: "14px",
              color: "#000000",
              outline: "none",
              minWidth: "200px",
              cursor: "pointer",
            }}
          >
            <option value="">Select a case</option>
            {cases.map((case_) => (
              <option key={case_.id} value={case_.id}>
                {case_.caseNumber} - {case_.clientName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCase && (
        <>
          {/* Bulk Actions */}
          <div className="bulk-actions" style={{ marginBottom: "12px" }}>
            <div 
              className="bulk-upload"
              onDragOver={handleUploadDragOver}
              onDragLeave={handleUploadDragLeave}
              onDrop={handleBulkUploadDrop}
              style={{ 
                border: "2px dashed #d1d5db",
                borderRadius: "8px",
                padding: "12px",
                textAlign: "center",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#3b82f6";
                e.currentTarget.style.backgroundColor = "#f0f9ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#d1d5db";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <input
                type="file"
                multiple
                onChange={handleBulkUpload}
                id="bulk-upload-input"
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.zip"
              />
              <label
                htmlFor="bulk-upload-input"
                className="bulk-upload-btn"
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                {processingStatus.isProcessing
                  ? "⏳ Processing..."
                  : "📁 Upload & Auto-Categorize"}
              </label>
              <div style={{ marginTop: "8px" }}>
                <span
                  className="upload-hint"
                  style={{ fontSize: "10px", display: "block" }}
                >
                  AI will suggest categories for your documents
                </span>
                <span
                  className="file-size-hint"
                  style={{ fontSize: "10px", display: "block" }}
                >
                  📏 Max 10MB per file
                </span>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#6b7280",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  💡 Drag & drop files here or click to browse
                </span>
              </div>
            </div>
            
            <button 
              onClick={handleBulkDownload}
              disabled={uploadedDocuments.length === 0 || bulkDownloading}
              style={{
                background:
                  uploadedDocuments.length === 0 || bulkDownloading
                    ? "rgba(156, 163, 175, 0.3)"
                    : "linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 100%)",
                border: "1px solid rgba(139, 92, 246, 0.3)",
                borderRadius: "12px",
                padding: "10px 16px",
                color:
                  uploadedDocuments.length === 0 || bulkDownloading
                    ? "#9ca3af"
                    : "white",
                fontSize: "14px",
                fontWeight: "500",
                cursor:
                  uploadedDocuments.length === 0 || bulkDownloading
                    ? "not-allowed"
                    : "pointer",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                boxShadow:
                  uploadedDocuments.length === 0 || bulkDownloading
                    ? "none"
                    : "0 2px 8px rgba(139, 92, 246, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              className="bulk-download-btn"
            >
              {bulkDownloading ? (
                <>
                  {React.createElement(FiClock as any, {
                    style: { fontSize: "14px" },
                  })}
                  Downloading...
                </>
              ) : (
                <>
                  {React.createElement(FiDownload as any, {
                    style: { fontSize: "14px", color: "#22c55e" },
                  })}
                  Download All
                </>
              )}
            </button>
          </div>

          {/* Top Panel - Document Categories */}
          <div
            className="required-documents-panel"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(139, 92, 246, 0.2)",
              borderRadius: "16px",
              marginBottom: "24px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div
              className="panel-header"
              style={{ padding: "16px 20px", marginBottom: "8px" }}
            >
              <h2 style={{ fontSize: "18px", margin: "0 0 4px 0" }}>
                Required Documents
              </h2>
              <p
                style={{
                  fontSize: "12px",
                  margin: "0 0 8px 0",
                  color: "#6b7280",
                }}
              >
                Add document categories and upload required files for your case.
              </p>
              <p
                className="file-size-limit"
                style={{
                  fontSize: "10px",
                  margin: "0 0 8px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {React.createElement(FiFile as any, {
                  style: { color: "#6b7280", fontSize: "12px" },
                })}
                <strong>File size limit:</strong> 10MB per file
              </p>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <button 
                  onClick={updateDocumentRequirements}
                  title="Refresh document status"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "12px",
                    padding: "8px 12px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#000000",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                  }}
                  className="refresh-btn"
                >
                  {React.createElement(FiRefreshCw as any, {
                    style: { fontSize: "14px" },
                  })}
                  Refresh Status
                </button>
              </div>
            </div>
            
            {/* Category Selection */}
            <div
              className="category-selection"
              style={{ padding: "0 20px 12px 20px" }}
            >
              <div className="category-selector">
                <select 
                  value={selectedCategoryId}
                  onChange={(e) => {
                    if (e.target.value === "custom") {
                      openCustomCategoryModal();
                    } else {
                      setSelectedCategoryId(e.target.value);
                    }
                  }}
                  className="category-dropdown"
                  style={{ fontSize: "12px", padding: "6px 8px" }}
                >
                  <option value="">Select Document Category</option>
                  {availableCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </option>
                  ))}
                  <option value="custom">
                    {React.createElement(FiPlus as any)} Create Custom Category
                  </option>
                </select>
                <button 
                  onClick={addCategory}
                  disabled={!selectedCategoryId}
                  style={{
                    background: !selectedCategoryId
                      ? "rgba(156, 163, 175, 0.3)"
                      : "linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 100%)",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                    borderRadius: "12px",
                    padding: "8px 12px",
                    color: !selectedCategoryId ? "#9ca3af" : "white",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: !selectedCategoryId ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    backdropFilter: "blur(10px)",
                    boxShadow: !selectedCategoryId
                      ? "none"
                      : "0 2px 8px rgba(139, 92, 246, 0.2)",
                  }}
                  className="add-category-btn"
                >
                  {React.createElement(FiPlus as any, {
                    style: { fontSize: "12px" },
                  })}
                  Add
                </button>
              </div>
            </div>
            
            <div
              className="document-requirements"
              style={{ padding: "0 20px 16px 20px" }}
            >
              {selectedCategories.map((req) => (
                <div
                  key={req.id}
                  className="document-requirement"
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}
                >
                  <div
                    className="requirement-info"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      className="category-icon"
                      style={{ fontSize: "16px" }}
                    >
                      {getCategoryIcon(req.category)}
                    </span>
                    <div className="requirement-details" style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: "14px",
                          margin: "0 0 2px 0",
                          fontWeight: "500",
                        }}
                      >
                        {req.name}
                      </h3>
                      <p
                        style={{
                          fontSize: "11px",
                          margin: "0",
                          color: "#6b7280",
                        }}
                      >
                        {req.description}
                      </p>
                    </div>
                  </div>
                  
                  <div
                    className="requirement-actions"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: "8px",
                    }}
                  >
                    <span
                      className={`status ${getStatusColor(req.status)}`}
                      style={{ fontSize: "11px" }}
                    >
                      {getStatusIcon(req.status)}{" "}
                      {req.status === "uploaded" ? "Uploaded" : "Pending"}
                    </span>
                    
                    {req.status === "pending" ? (
                      <div 
                        className="upload-action"
                        onDragOver={handleUploadDragOver}
                        onDragLeave={handleUploadDragLeave}
                        onDrop={(e) => handleUploadDrop(e, req.id)}
                        style={{ 
                          border: "2px dashed #d1d5db",
                          borderRadius: "6px",
                          padding: "6px",
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#3b82f6";
                          e.currentTarget.style.backgroundColor = "#f0f9ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#d1d5db";
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <input
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              handleFileUpload(req.id, files);
                            }
                          }}
                          id={`upload-${req.id}`}
                          style={{ display: "none" }}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.zip"
                        />
                        <label
                          htmlFor={`upload-${req.id}`}
                          className="upload-btn"
                          style={{ fontSize: "11px", padding: "4px 8px" }}
                        >
                          {uploading === req.id
                            ? "⏳ Uploading..."
                            : "📤 Upload Files"}
                        </label>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6b7280",
                            marginTop: "2px",
                          }}
                        >
                          💡 Drag & drop here
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="upload-action"
                        onDragOver={handleUploadDragOver}
                        onDragLeave={handleUploadDragLeave}
                        onDrop={(e) => handleUploadDrop(e, req.id)}
                        style={{ 
                          border: "2px dashed #d1d5db",
                          borderRadius: "6px",
                          padding: "6px",
                          transition: "all 0.2s ease",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#3b82f6";
                          e.currentTarget.style.backgroundColor = "#f0f9ff";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "#d1d5db";
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <input
                          type="file"
                          multiple
                          onChange={(e) => {
                            const files = e.target.files;
                            if (files && files.length > 0) {
                              handleFileUpload(req.id, files);
                            }
                          }}
                          id={`upload-more-${req.id}`}
                          style={{ display: "none" }}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.webp,.zip"
                        />
                        <label
                          htmlFor={`upload-more-${req.id}`}
                          className="upload-btn"
                          style={{ fontSize: "11px", padding: "4px 8px" }}
                        >
                          {uploading === req.id
                            ? "⏳ Uploading..."
                            : "📤 Upload More"}
                        </label>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6b7280",
                            marginTop: "2px",
                          }}
                        >
                          💡 Drag & drop here
                        </div>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => removeCategory(req.id)}
                      disabled={req.status === "uploaded"}
                      title={
                        req.status === "uploaded"
                          ? "Cannot remove category with uploaded documents"
                          : "Remove category"
                      }
                      style={{
                        background:
                          req.status === "uploaded"
                            ? "rgba(156, 163, 175, 0.3)"
                            : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                        border: "1px solid rgba(220, 38, 38, 0.3)",
                        borderRadius: "8px",
                        padding: "4px 8px",
                        color: req.status === "uploaded" ? "#9ca3af" : "white",
                        fontSize: "12px",
                        fontWeight: "500",
                        cursor:
                          req.status === "uploaded" ? "not-allowed" : "pointer",
                        transition: "all 0.3s ease",
                        backdropFilter: "blur(10px)",
                        boxShadow:
                          req.status === "uploaded"
                            ? "none"
                            : "0 2px 8px rgba(220, 38, 38, 0.2)",
                      }}
                      className={`remove-category-btn ${
                        req.status === "uploaded" ? "disabled" : ""
                      }`}
                    >
                      {React.createElement(FiMinus as any, {
                        style: { fontSize: "12px" },
                      })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Panel - Uploaded Documents */}
          <div
            className="uploaded-documents-panel"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(139, 92, 246, 0.2)",
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div
              className="panel-header"
              style={{ padding: "16px 20px", marginBottom: "8px" }}
            >
              <h2 style={{ fontSize: "18px", margin: "0 0 8px 0" }}>
                Uploaded Documents
              </h2>

              <div
                className="panel-controls"
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                {/* Merge PDFs Button - Always visible when there are PDFs */}
                {filteredDocuments.filter((doc) =>
                  doc.fileName.toLowerCase().endsWith(".pdf")
                ).length >= 2 && (
                  <button 
                    onClick={() => {
                      // For "All Categories", we'll merge all PDFs across categories
                      const categoryId =
                        selectedCategory === "All Categories"
                          ? "all_categories"
                          : selectedCategory;
                      openMergeModal(categoryId);
                    }}
                    className="merge-pdfs-btn"
                    title="Merge selected PDFs from different categories"
                    style={{
                      fontSize: "12px",
                      padding: "6px 12px",
                      backgroundColor: "#185adb",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                    }}
                  >
                    {React.createElement(FiLink as any, {
                      style: { fontSize: "14px", color: "#7c3aed" },
                    })}
                    Merge PDFs
                  </button>
                )}
                
                {/* Delete Documents Button - Always visible when documents are selected */}
                {selectedDocumentsForDelete.length > 0 && (
                  <button 
                    onClick={handleBulkDelete}
                    disabled={deleting}
                    style={{
                      background: deleting
                        ? "rgba(156, 163, 175, 0.3)"
                        : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      border: "1px solid rgba(220, 38, 38, 0.3)",
                      borderRadius: "12px",
                      padding: "8px 12px",
                      color: deleting ? "#9ca3af" : "white",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: deleting ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      backdropFilter: "blur(10px)",
                      boxShadow: deleting
                        ? "none"
                        : "0 2px 8px rgba(220, 38, 38, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    className="delete-documents-btn"
                    title={`Delete ${
                      selectedDocumentsForDelete.length
                    } selected document${
                      selectedDocumentsForDelete.length > 1 ? "s" : ""
                    }`}
                  >
                    {deleting ? (
                      <>
                        {React.createElement(FiClock as any, {
                          style: { fontSize: "14px" },
                        })}
                        Deleting...
                      </>
                    ) : (
                      <>
                        {React.createElement(FiTrash2 as any, {
                          style: { fontSize: "14px", color: "#ef4444" },
                        })}
                        Delete ({selectedDocumentsForDelete.length})
                      </>
                    )}
                  </button>
                )}
                
                {/* Download Documents Button - Always visible when documents are selected */}
                {selectedDocumentsForDownload.length > 0 && (
                  <button 
                    onClick={handleBulkDownloadSelected}
                    disabled={downloading}
                    style={{
                      background: downloading
                        ? "rgba(156, 163, 175, 0.3)"
                        : "linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 100%)",
                      border: "1px solid rgba(139, 92, 246, 0.3)",
                      borderRadius: "12px",
                      padding: "8px 12px",
                      color: downloading ? "#9ca3af" : "white",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: downloading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      backdropFilter: "blur(10px)",
                      boxShadow: downloading
                        ? "none"
                        : "0 2px 8px rgba(139, 92, 246, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    className="download-documents-btn"
                    title={`Download ${
                      selectedDocumentsForDownload.length
                    } selected document${
                      selectedDocumentsForDownload.length > 1 ? "s" : ""
                    }`}
                  >
                    {downloading ? (
                      <>
                        {React.createElement(FiClock as any, {
                          style: { fontSize: "14px" },
                        })}
                        Downloading...
                      </>
                    ) : (
                      <>
                        {React.createElement(FiDownload as any, {
                          style: { fontSize: "14px", color: "#22c55e" },
                        })}
                        Download ({selectedDocumentsForDownload.length})
                      </>
                    )}
                  </button>
                )}
                
                <select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-filter"
                  style={{ fontSize: "12px", padding: "6px 8px" }}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ fontSize: "12px", padding: "6px 8px" }}
                  />
                  <span className="search-icon">🔍</span>
                </div>
              </div>
            </div>

            <div
              className="uploaded-documents-list"
              style={{ padding: "0 20px 16px 20px" }}
            >
              {filteredDocuments.length === 0 ? (
                <div
                  className="empty-state"
                  style={{ padding: "20px", textAlign: "center" }}
                >
                  <div
                    className="empty-icon"
                    style={{
                      fontSize: "32px",
                      marginBottom: "8px",
                      color: "#8b5cf6",
                    }}
                  >
                    {React.createElement(FiFile as any, {
                      style: { fontSize: "32px" },
                    })}
                  </div>
                  <h3 style={{ fontSize: "14px", margin: "0 0 4px 0" }}>
                    No documents uploaded yet
                  </h3>
                  <p
                    style={{ fontSize: "11px", color: "#6b7280", margin: "0" }}
                  >
                    Upload your first document using the form above
                  </p>
                </div>
              ) : (
                <div className="cases-table">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ fontSize: "12px", padding: "8px" }}>
                          DOCUMENT NAME
                        </th>
                        <th style={{ fontSize: "12px", padding: "8px" }}>
                          TYPE
                        </th>
                        <th style={{ fontSize: "12px", padding: "8px" }}>
                          SIZE
                        </th>
                        <th style={{ fontSize: "12px", padding: "8px" }}>
                          UPLOADED
                        </th>
                        <th style={{ fontSize: "12px", padding: "8px", textAlign: "center" }}>
                          ACTIONS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((doc) => (
                        <tr key={doc.id}>
                          <td>
                            <div className="client-info">
                              <div
                                className="client-avatar"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background:
                                    "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                                  border: "1px solid rgba(139, 92, 246, 0.2)",
                                }}
                              >
                                {React.createElement(FiFile as any, {
                                  style: { color: "#8b5cf6", fontSize: "16px" },
                                })}
                              </div>
                              <div>
                                <div
                                  className="client-name"
                                  style={{ fontSize: "13px" }}
                                >
                                  {doc.fileName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className="priority-badge"
                              style={{ fontSize: "10px" }}
                            >
                              {doc.documentType}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{ fontSize: "11px", color: "#6b7280" }}
                            >
                              {formatFileSize(doc.fileSize)}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{ fontSize: "11px", color: "#6b7280" }}
                            >
                              {formatDate(doc.uploadedAt)}
                            </span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <div className="action-buttons" style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              flexWrap: "nowrap",
                              whiteSpace: "nowrap"
                            }}>
                              {/* Checkbox for PDF merging */}
                              {doc.fileName.toLowerCase().endsWith(".pdf") && (
                                <div
                                  className="merge-checkbox-container"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    flexShrink: 0
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedDocumentsForMerge.includes(
                                      doc.id
                                    )}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDocumentsForMerge((prev) => [
                                          ...prev,
                                          doc.id,
                                        ]);
                                      } else {
                                        setSelectedDocumentsForMerge((prev) =>
                                          prev.filter((id) => id !== doc.id)
                                        );
                                      }
                                    }}
                                    className="merge-checkbox"
                                    title="Select this PDF for merging with other documents"
                                    style={{ 
                                      width: "16px",
                                      height: "16px",
                                      accentColor: "#185adb",
                                      cursor: "pointer",
                                    }}
                                  />
                                  <span 
                                    className="merge-hint" 
                                    title="Select this PDF for merging with other documents"
                                    style={{ 
                                      fontSize: "12px",
                                      color: "#7c3aed",
                                      cursor: "help",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {React.createElement(FiLink as any)}
                                  </span>
                                </div>
                              )}
                              
                              {/* Checkbox for bulk deletion */}
                              <div
                                className="delete-checkbox-container"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  flexShrink: 0
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDocumentsForDelete.includes(
                                    doc.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDocumentsForDelete((prev) => [
                                        ...prev,
                                        doc.id,
                                      ]);
                                    } else {
                                      setSelectedDocumentsForDelete((prev) =>
                                        prev.filter((id) => id !== doc.id)
                                      );
                                    }
                                  }}
                                  className="delete-checkbox"
                                  title="Select this document for deletion"
                                  style={{ 
                                    width: "16px",
                                    height: "16px",
                                    accentColor: "#dc2626",
                                    cursor: "pointer",
                                  }}
                                />
                                <span 
                                  className="delete-hint" 
                                  title="Select this document for deletion"
                                  style={{ 
                                    fontSize: "12px",
                                    color: "#ef4444",
                                    cursor: "help",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {React.createElement(FiTrash2 as any)}
                                </span>
                              </div>
                              
                              {/* Checkbox for bulk download */}
                              <div
                                className="download-checkbox-container"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  flexShrink: 0
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDocumentsForDownload.includes(
                                    doc.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDocumentsForDownload(
                                        (prev) => [...prev, doc.id]
                                      );
                                    } else {
                                      setSelectedDocumentsForDownload((prev) =>
                                        prev.filter((id) => id !== doc.id)
                                      );
                                    }
                                  }}
                                  className="download-checkbox"
                                  title="Select this document for download"
                                  style={{ 
                                    width: "16px",
                                    height: "16px",
                                    accentColor: "#059669",
                                    cursor: "pointer",
                                  }}
                                />
                                <span 
                                  className="download-hint" 
                                  title="Select this document for download"
                                  style={{ 
                                    fontSize: "12px",
                                    color: "#22c55e",
                                    cursor: "help",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  {React.createElement(FiDownload as any)}
                                </span>
                              </div>
                              
                              <button 
                                onClick={() =>
                                  handleViewDocument(doc.id, doc.fileName)
                                }
                                title={getViewButtonTitle(doc.fileName)}
                                style={{
                                  background: "rgba(255, 255, 255, 0.1)",
                                  border: "1px solid rgba(255, 255, 255, 0.2)",
                                  borderRadius: "8px",
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  color: "#000000",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap",
                                  backdropFilter: "blur(10px)",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                                className="action-btn"
                              >
                                {getViewButtonText(doc.fileName)}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!selectedCase && (
        <div className="no-case-selected">
          <div className="empty-icon">📋</div>
          <h3>No case selected</h3>
          <p>Please select a case to manage its documents</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="modal-header">
              <div className="modal-icon">🔗</div>
              <h3>Delete Document</h3>
              <button 
                onClick={cancelDelete}
                title="Close"
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  padding: "6px",
                  fontSize: "16px",
                  color: "#000000",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                }}
                className="modal-close-btn"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>Are you sure you want to delete this document?</p>
              <div className="document-info">
                <strong>{deleteModal.fileName}</strong>
              </div>
              <p className="bulk-delete-warning">
                This action can't be undone.
              </p>
            </div>
            
            <div className="bulk-delete-actions">
              <button 
                onClick={cancelDelete}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "12px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#000000",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                style={{
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  border: "1px solid rgba(220, 38, 38, 0.3)",
                  borderRadius: "12px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "white",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 2px 8px rgba(220, 38, 38, 0.2)",
                }}
                className="delete-confirm-btn"
              >
                Delete Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Type Modal */}
      {fileTypeModal.isOpen && (
        <div className="modal-overlay" onClick={cancelFileTypeModal}>
          <div className="file-type-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-icon">🔗</div>
              <div className="modal-icon">📄</div>
              <h3>Document Preview</h3>
              <button 
                onClick={cancelFileTypeModal}
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="file-preview-info">
                <div className="file-icon">📄</div>
                <div className="file-details">
                  <h4>{fileTypeModal.fileName}</h4>
                  <p className="file-type-info">
                    This file type cannot be previewed directly in the browser
                  </p>
                </div>
              </div>
              
              <div className="supported-formats">
                <h5>📋 Supported Preview Formats:</h5>
                <div className="format-list">
                  <span className="format-item">PDF</span>
                  <span className="format-item">
                    Images (JPG, PNG, GIF, BMP, WebP)
                  </span>
                  <span className="format-item">Text files</span>
                </div>
              </div>
              
              <div className="action-suggestion">
                <p>
                  💡 <strong>Tip:</strong> You can download this file and open
                  it with the appropriate application on your device.
                </p>
              </div>
            </div>
            
            <div className="bulk-delete-actions">
              <button 
                onClick={cancelFileTypeModal}
                className="bulk-delete-cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={handleFileTypeDownload}
                className="download-confirm-btn"
              >
                {React.createElement(FiDownload as any, {
                  style: { fontSize: "14px", color: "#22c55e" },
                })}
                Download File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Size Error Modal */}
      {fileSizeErrorModal.isOpen && (
        <div
          className="modal-overlay"
          onClick={() =>
            setFileSizeErrorModal({ ...fileSizeErrorModal, isOpen: false })
          }
        >
          <div
            className="file-size-error-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-icon">🔗</div>
              <div className="modal-icon">⚠️</div>
              <h3>File Too Large</h3>
              <button 
                onClick={closeFileSizeErrorModal}
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="file-error-info">
                <div className="file-icon">📄</div>
                <div className="file-details">
                  <h4>{fileSizeErrorModal.fileName}</h4>
                  <p className="file-size-error">
                    Size:{" "}
                    <strong>
                      {formatFileSize(fileSizeErrorModal.fileSize)}
                    </strong>
                  </p>
                </div>
              </div>
              
              <div className="size-limit-info">
                <h5>📏 Size Limit Exceeded</h5>
                <p>
                  This file exceeds the maximum allowed size of{" "}
                  <strong>2 MB</strong>.
                </p>
                <p>
                  Please choose a smaller file or compress the document before
                  uploading.
                </p>
              </div>
              
              <div className="helpful-tips">
                <h5>💡 Tips to Reduce File Size:</h5>
                <ul>
                  <li>Compress images to JPEG format</li>
                  <li>Reduce image resolution to 72-150 DPI</li>
                  <li>Use PDF compression tools</li>
                  <li>Remove unnecessary content from documents</li>
                </ul>
              </div>
            </div>
            
            <div className="bulk-delete-actions">
              <button 
                onClick={closeFileSizeErrorModal}
                className="close-error-btn"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categorization Review Modal */}
      {categorizationModal.isOpen && (
        <div className="modal-overlay" onClick={closeCategorizationModal}>
          <div
            className="categorization-review-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-icon">🔗</div>
              <div className="modal-icon">📄</div>
              <h3>Review and Upload Documents</h3>
              <button 
                onClick={closeCategorizationModal}
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>
                Please review the suggested categories for each document and
                confirm their final category.
              </p>
              <p>
                You can click "Accept All" to upload all documents with their
                suggested categories, or "Review Each" to manually adjust each
                one.
              </p>
              
              <div className="documents-to-review">
                {categorizationModal.documents.map((doc, index) => (
                  <div key={doc.file.name} className="document-review-item">
                    <div className="document-preview">
                      <div className="file-icon" style={{ color: "#8b5cf6" }}>
                        {React.createElement(FiFile as any, {
                          style: { fontSize: "24px" },
                        })}
                      </div>
                      <div className="file-details">
                        <h4>{doc.file.name}</h4>
                        <p>
                          Suggested Category:{" "}
                          <strong>{doc.suggestedCategory}</strong>
                        </p>
                        <p>
                          Confidence:{" "}
                          <strong>{doc.confidence.toFixed(2)}%</strong>
                        </p>
                      </div>
                    </div>
                    <div className="review-actions">
                                             <select 
                         value={doc.finalCategory} 
                        onChange={(e) =>
                          updateDocumentCategory(index, e.target.value)
                        }
                         className="category-select"
                       >
                         <option value="passport">Passport</option>
                        <option value="birth_certificate">
                          Birth Certificate
                        </option>
                        <option value="marriage_certificate">
                          Marriage Certificate
                        </option>
                        <option value="employment_letter">
                          Employment Letter
                        </option>
                         <option value="bank_statements">Bank Statements</option>
                         <option value="tax_returns">Tax Returns</option>
                         <option value="medical_records">Medical Records</option>
                        <option value="police_clearance">
                          Police Clearance
                        </option>
                         <option value="other">Other</option>
                       </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bulk-delete-actions">
              <button 
                onClick={closeCategorizationModal}
                className="bulk-delete-cancel-btn"
              >
                Cancel
              </button>
              <button 
                onClick={acceptAllCategorizations}
                className="accept-all-btn"
                disabled={processingStatus.isProcessing}
              >
                {processingStatus.isProcessing
                  ? "⏳ Accepting..."
                  : "Accept All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Status Modal */}
      {processingStatus.isProcessing && (
        <div
          className="modal-overlay"
          onClick={() =>
            setProcessingStatus({ ...processingStatus, isProcessing: false })
          }
        >
          <div
            className="processing-status-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-icon" style={{ color: "#8b5cf6" }}>
                {React.createElement(FiClock as any, {
                  style: { fontSize: "24px" },
                })}
              </div>
              <h3>Processing Documents</h3>
              <button 
                onClick={() =>
                  setProcessingStatus({
                    ...processingStatus,
                    isProcessing: false,
                  })
                }
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p>{processingStatus.message}</p>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${processingStatus.progress}%` }}
                ></div>
              </div>
              <p>
                Processed: <strong>{processingStatus.processedCount}</strong> /{" "}
                <strong>{processingStatus.totalCount}</strong>
              </p>
            </div>
            
            <div className="bulk-delete-actions">
              <button 
                onClick={() =>
                  setProcessingStatus({
                    ...processingStatus,
                    isProcessing: false,
                  })
                }
                className="bulk-delete-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteModal.isOpen && (
        <div
          className="modal-overlay"
          onClick={cancelBulkDelete}
          style={{
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            className="delete-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "16px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              border: "1px solid rgba(229, 231, 235, 0.8)",
              maxWidth: "480px",
              width: "90vw",
              maxHeight: "85vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="modal-header"
              style={{
                padding: "24px 24px 0 24px",
                borderBottom: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                    border: "1px solid #fecaca",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {React.createElement(FiTrash2 as any, {
                    style: { color: "#ef4444", fontSize: "20px" },
                  })}
                </div>
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    Delete Documents
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#6b7280",
                    }}
                  >
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button 
                onClick={cancelBulkDelete}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  color: "#9ca3af",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.color = "#374151";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "none";
                  e.currentTarget.style.color = "#9ca3af";
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div
              className="modal-content"
              style={{
                padding: "20px 24px",
                flex: 1,
                overflow: "auto",
              }}
            >
              <p
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "16px",
                  color: "#374151",
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to delete these{" "}
                {bulkDeleteModal.documents.length} document
                {bulkDeleteModal.documents.length > 1 ? "s" : ""}?
              </p>

              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "20px",
                  maxHeight: "200px",
                  overflow: "auto",
                }}
              >
                {bulkDeleteModal.documents.map((doc, index) => (
                  <div
                    key={doc.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom:
                        index < bulkDeleteModal.documents.length - 1
                          ? "1px solid #e5e7eb"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background:
                          "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                        border: "1px solid rgba(139, 92, 246, 0.2)",
                        marginRight: "12px",
                        flexShrink: 0,
                      }}
                    >
                      {React.createElement(FiFile as any, {
                        style: { color: "#8b5cf6", fontSize: "16px" },
                      })}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: "500",
                          color: "#111827",
                          fontSize: "14px",
                          marginBottom: "2px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {doc.fileName}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                        }}
                      >
                        {doc.documentType}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {React.createElement(FiAlertTriangle as any, {
                  style: { color: "#ef4444", fontSize: "16px", flexShrink: 0 },
                })}
                <span
                  style={{
                    fontSize: "14px",
                    color: "#dc2626",
                    fontWeight: "500",
                  }}
                >
                  This action cannot be undone
                </span>
              </div>
            </div>
            
            <div
              className="modal-actions"
              style={{
                padding: "20px 24px 24px 24px",
                borderTop: "1px solid #f3f4f6",
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              <button 
                onClick={cancelBulkDelete}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  background: "white",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f9fafb";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmBulkDelete}
                className="bulk-delete-confirm-btn"
                disabled={deleting}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "8px",
                  background: deleting ? "#f87171" : "#ef4444",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: deleting ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  opacity: deleting ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  if (!deleting) {
                    e.currentTarget.style.background = "#dc2626";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleting) {
                    e.currentTarget.style.background = "#ef4444";
                  }
                }}
              >
                {deleting ? (
                  <>
                    {React.createElement(FiClock as any, {
                      style: { fontSize: "14px" },
                    })}
                    Deleting...
                  </>
                ) : (
                  <>
                    {React.createElement(FiTrash2 as any, {
                      style: { fontSize: "14px" },
                    })}
                    Delete {bulkDeleteModal.documents.length} Document
                    {bulkDeleteModal.documents.length > 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple Success Modal */}
      {successModal.isOpen && (
        <div
          className="modal-overlay"
          onClick={closeSuccessModal}
          style={{
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div
            className="simple-success-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "16px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              border: "1px solid rgba(229, 231, 235, 0.8)",
              maxWidth: "420px",
              width: "90vw",
              overflow: "hidden",
            }}
          >
            <div
              style={{ padding: "32px 24px 24px 24px", textAlign: "center" }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "16px",
                  margin: "0 auto 20px auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    successModal.message?.toLowerCase().includes("download") &&
                    successModal.message?.toLowerCase().includes("progress")
                      ? "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)"
                      : successModal.message
                          ?.toLowerCase()
                          .includes("download") &&
                        successModal.documentCount > 0
                      ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                      : successModal.documentCount === 0
                      ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
                      : "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  border:
                    successModal.message?.toLowerCase().includes("download") &&
                    successModal.message?.toLowerCase().includes("progress")
                      ? "1px solid #d1d5db"
                      : successModal.message
                          ?.toLowerCase()
                          .includes("download") &&
                        successModal.documentCount > 0
                      ? "1px solid #bbf7d0"
                      : successModal.documentCount === 0
                      ? "1px solid #fecaca"
                      : "1px solid #bbf7d0",
                }}
              >
                {successModal.message?.toLowerCase().includes("download") &&
                successModal.message?.toLowerCase().includes("progress")
                  ? React.createElement(FiClock as any, {
                      style: { fontSize: "28px", color: "#6b7280" },
                    })
                  : successModal.message?.toLowerCase().includes("download") &&
                    successModal.documentCount > 0
                  ? React.createElement(FiDownload as any, {
                      style: { fontSize: "28px", color: "#059669" },
                    })
                  : successModal.documentCount === 0
                  ? React.createElement(FiAlertTriangle as any, {
                      style: { fontSize: "28px", color: "#dc2626" },
                    })
                  : React.createElement(FiCheck as any, {
                      style: { fontSize: "28px", color: "#059669" },
                    })}
              </div>

              <h3
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                {successModal.documentCount === 0 &&
                !successModal.message?.toLowerCase().includes("progress")
                  ? "Error"
                  : successModal.message?.toLowerCase().includes("progress")
                  ? "Processing..."
                  : successModal.message?.toLowerCase().includes("download")
                  ? "Download Complete"
                  : "Success"}
              </h3>

              <p
                style={{
                  margin: "0 0 24px 0",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: "1.5",
                }}
              >
                {successModal.message}
              </p>

              {!successModal.message?.toLowerCase().includes("progress") && (
                <button 
                  onClick={closeSuccessModal}
                  style={{ 
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "8px",
                    background:
                      successModal.documentCount === 0 ? "#ef4444" : "#8b5cf6",
                    color: "white",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      successModal.documentCount === 0 ? "#dc2626" : "#7c3aed";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      successModal.documentCount === 0 ? "#ef4444" : "#8b5cf6";
                  }}
                >
                  {successModal.documentCount === 0 ? "Close" : "Continue"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quality Error Modal */}
      <QualityErrorModal
        isOpen={qualityErrorModal.isOpen}
        fileName={qualityErrorModal.fileName}
        qualityScore={qualityErrorModal.qualityScore}
        failureReason={qualityErrorModal.failureReason}
        recommendation={qualityErrorModal.recommendation}
        onClose={qualityErrorModal.onClose}
        requiresConfirmation={qualityErrorModal.requiresConfirmation}
        onProceed={qualityErrorModal.onProceed}
      />

      {/* Custom Category Modal */}
      {customCategoryModal.isOpen && (
        <div className="modal-overlay" onClick={closeCustomCategoryModal}>
          <div
            className="custom-category-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-icon" style={{ color: "#8b5cf6" }}>
                {React.createElement(FiFileText as any, {
                  style: { fontSize: "24px" },
                })}
              </div>
              <h3>Create Custom Document Category</h3>
              <button 
                onClick={closeCustomCategoryModal}
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="category-name">Category Name *</label>
                <input
                  type="text"
                  id="category-name"
                  value={customCategoryModal.name}
                  onChange={(e) =>
                    setCustomCategoryModal((prev) => ({
                    ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="e.g., Employment Contract, Rental Agreement"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="category-description">
                  Description (Optional)
                </label>
                <textarea
                  id="category-description"
                  value={customCategoryModal.description}
                  onChange={(e) =>
                    setCustomCategoryModal((prev) => ({
                    ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Brief description of this document type"
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="bulk-delete-actions">
              <button 
                onClick={closeCustomCategoryModal}
                className="bulk-delete-cancel-btn"
              >
                Cancel
              </button>
              <button onClick={createCustomCategory} className="create-btn">
                Create Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Merge Modal */}
      {mergeModal.isOpen && (
        <div className="modal-overlay" onClick={closeMergeModal}>
          <div
            className="pdf-merge-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "90vh",
              height: "auto",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div className="modal-header">
              <div className="modal-icon" style={{ color: "#8b5cf6" }}>
                {React.createElement(FiLink as any, {
                  style: { fontSize: "24px" },
                })}
              </div>
              <h3 style={{ textAlign: "center", flex: 1, margin: "0 auto" }}>
                Merge PDF Documents
              </h3>
              <button 
                onClick={closeMergeModal}
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div
              className="modal-content"
              style={{
                flex: 1,
                overflow: "auto",
                padding: "0 20px",
                maxHeight: "calc(90vh - 120px)",
              }}
            >
              {/* Document Selection */}
              <div className="merge-section">
                <h4>Select Documents to Merge</h4>
                {mergeModal.categoryId === "all_categories" && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "12px",
                    }}
                  >
                    <strong>Cross-Category Merge:</strong> You can select PDFs
                    from different document categories to merge them together.
                  </p>
                )}
                <div className="document-selection-list">
                  {(mergeModal.categoryId === "all_categories"
                    ? uploadedDocuments
                    : filteredDocuments
                  )
                    .filter((doc) =>
                      doc.fileName.toLowerCase().endsWith(".pdf")
                    )
                    .map((doc) => (
                      <div key={doc.id} className="document-selection-item">
                        <input
                          type="checkbox"
                          checked={selectedDocumentsForMerge.includes(doc.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDocumentsForMerge((prev) => [
                                ...prev,
                                doc.id,
                              ]);
                            } else {
                              setSelectedDocumentsForMerge((prev) =>
                                prev.filter((id) => id !== doc.id)
                              );
                            }
                          }}
                          className="merge-checkbox"
                        />
                        <div
                          className="file-icon"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            borderRadius: "6px",
                            background:
                              "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
                            border: "1px solid rgba(139, 92, 246, 0.2)",
                            marginRight: "8px",
                          }}
                        >
                          {React.createElement(FiFile as any, {
                            style: { color: "#8b5cf6", fontSize: "14px" },
                          })}
                        </div>
                        <div className="document-details">
                          <strong>{doc.fileName}</strong>
                          <small>
                            {formatDate(doc.uploadedAt)} •{" "}
                            {formatFileSize(doc.fileSize)}
                            {mergeModal.categoryId === "all_categories" && (
                              <span style={{ color: "#185adb" }}>
                                {" "}
                                • {doc.documentType}
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Merge Order */}
              <div className="merge-section">
                <h4>Merge Order</h4>
                <div className="merge-order-controls">
                  <label>
                    <input
                      type="radio"
                      value="date"
                      checked={mergeOrder === "date"}
                      onChange={(e) => setMergeOrder(e.target.value as any)}
                    />
                    By Date (oldest first)
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="filename"
                      checked={mergeOrder === "filename"}
                      onChange={(e) => setMergeOrder(e.target.value as any)}
                    />
                    By Filename (A-Z)
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="custom"
                      checked={mergeOrder === "custom"}
                      onChange={(e) => setMergeOrder(e.target.value as any)}
                    />
                    Custom Order (drag to reorder)
                  </label>
                </div>

                {/* Custom Order Interface */}
                {mergeOrder === "custom" &&
                  selectedDocumentsForMerge.length > 0 && (
                  <div className="custom-order-interface">
                    <p>Drag to reorder documents:</p>
                    <div className="custom-order-list">
                      {selectedDocumentsForMerge.map((docId, index) => {
                          const doc = filteredDocuments.find(
                            (d) => d.id === docId
                          );
                        return doc ? (
                          <div
                            key={docId}
                            className={`custom-order-item ${
                                draggedItem === docId ? "dragging" : ""
                              } ${dragOverItem === docId ? "drag-over" : ""}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, docId)}
                            onDragOver={(e) => handleDragOver(e, docId)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, docId)}
                            onDragEnd={() => setDraggedItem(null)}
                          >
                            <span className="order-number">{index + 1}.</span>
                            <span className="file-name">{doc.fileName}</span>
                              <span
                                className="drag-handle"
                                title="Drag to reorder"
                              >
                                ⋮⋮
                              </span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Compression Size Selection */}
              <div className="merge-section">
                <h4>🗜️ Compression Settings</h4>
                <div className="compression-controls">
                  <label htmlFor="compression-size">
                    Target file size limit:
                  </label>
                  <select
                    id="compression-size"
                    value={compressionSize}
                    onChange={(e) => setCompressionSize(Number(e.target.value))}
                    className="compression-select"
                  >
                    <option value={2}>2 MB</option>
                    <option value={3}>3 MB</option>
                    <option value={4}>4 MB</option>
                    <option value={5}>5 MB</option>
                  </select>
                  <p className="compression-info">
                    "✓ File will be compressed to meet the selected size limit"
                  </p>
                </div>
              </div>

              {/* Custom Filename */}
              <div className="merge-section">
                <h4>📝 Output Settings</h4>
                <div className="filename-controls">
                  <label htmlFor="custom-filename">
                    Custom filename (optional):
                  </label>
                  <input
                    id="custom-filename"
                    type="text"
                    value={customFileName}
                    onChange={(e) => setCustomFileName(e.target.value)}
                    placeholder="e.g., Combined_Documents_2024"
                    className="filename-input"
                  />
                  <p className="filename-info">
                    Leave empty for auto-generated name:
                    "merged_[category]_[timestamp].pdf"
                  </p>
                </div>
              </div>

              {/* Merge Button */}
              <div className="merge-actions">
                <button 
                  onClick={() => handleMergePDFs(mergeModal.categoryId)}
                  disabled={merging || selectedDocumentsForMerge.length < 2}
                  className={`merge-btn ${
                    selectedDocumentsForMerge.length >= 2
                      ? "enabled"
                      : "disabled"
                  }`}
                >
                  {merging ? (
                    <>
                      {React.createElement(FiClock as any, {
                        style: { fontSize: "14px", marginRight: "6px" },
                      })}
                      Merging & Compressing...
                    </>
                  ) : (
                    <>
                      {React.createElement(FiLink as any, {
                        style: {
                          fontSize: "14px",
                          marginRight: "6px",
                          color: "#7c3aed",
                        },
                      })}
                      Merge {selectedDocumentsForMerge.length} PDFs
                    </>
                  )}
                </button>
                <p
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    margin: "8px 0 0 0",
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  {merging
                    ? "Compressing to {compressionSize}MB target size..."
                    : "PDFs will be compressed to {compressionSize}MB or less using multiple strategies"}
                </p>
              </div>

              {/* Merged Documents */}
              <div className="merge-section">
                <h4>Merged Documents</h4>
                {mergedDocuments.length === 0 ? (
                  <p className="no-merged-docs">No merged documents yet.</p>
                ) : (
                  <div className="merged-documents-list">
                    {mergedDocuments.map((merged) => (
                      <div key={merged.id} className="merged-document-item">
                        <div className="merged-doc-info">
                          <strong>
                            {merged.fileName ||
                              `${merged.categoryId}_merged.pdf`}
                          </strong>
                          <small>
                            {merged.totalPages} pages •{" "}
                            {Math.round(merged.fileSize / 1024)} KB •{" "}
                            {new Date(merged.createdAt).toLocaleDateString()}
                            {merged.fileSize <= 2 * 1024 * 1024 ? (
                              <span
                                style={{ color: "#059669", fontWeight: "bold" }}
                              >
                                {" "}
                                ✓ Size Optimized
                              </span>
                            ) : (
                              <span
                                style={{ color: "#dc2626", fontWeight: "bold" }}
                              >
                                {" "}
                                ⚠️ Exceeds 2MB Limit
                              </span>
                            )}
                          </small>
                        </div>
                        <div className="merged-doc-actions">
                          <button 
                            onClick={() => handleDownloadMerged(merged.id)}
                            className="download-btn"
                            title="Download merged PDF"
                          >
                            {React.createElement(FiDownload as any, {
                              style: {
                                fontSize: "14px",
                                marginRight: "6px",
                                color: "#22c55e",
                              },
                            })}
                            Download
                          </button>
                          <button 
                            onClick={() => handleDeleteMerged(merged.id)}
                            className="delete-btn"
                            title="Delete merged PDF"
                          >
                            {React.createElement(FiTrash2 as any, {
                              style: {
                                fontSize: "14px",
                                marginRight: "6px",
                                color: "#ef4444",
                              },
                            })}
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div
              className="modal-actions"
              style={{
                padding: "16px 20px",
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
                flexShrink: 0,
              }}
            >
              <button 
                onClick={closeMergeModal}
                className="bulk-delete-cancel-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Detection Modal */}
      <DuplicateDetectionModal
        isOpen={duplicateModal.isOpen}
        duplicateFiles={duplicateModal.duplicateFiles}
        onClose={() => setDuplicateModal({ isOpen: false, duplicateFiles: [] })}
      />

      {/* Deletion Progress Modal */}
      <DeletionProgressModal
        isOpen={deletionProgress.isOpen}
        isDeleting={deletionProgress.isDeleting}
        totalDocuments={deletionProgress.totalDocuments}
        deletedCount={deletionProgress.deletedCount}
        currentFileName={deletionProgress.currentFileName}
        onComplete={() =>
          setDeletionProgress((prev) => ({ ...prev, isOpen: false }))
        }
      />
    </div>
  );
} 
